import type { TranslationKeys } from "../types.js";
import { marketing as marketingEs } from "./marketing/es.js";

export const es: TranslationKeys = {
  common: {
    loading: "Cargando...",
    error: "Error",
    success: "Éxito",
    cancel: "Cancelar",
    confirm: "Confirmar",
    save: "Guardar",
    delete: "Eliminar",
    edit: "Editar",
    create: "Crear",
    update: "Actualizar",
    search: "Buscar",
    filter: "Filtrar",
    actions: "Acciones",
    back: "Volver",
    next: "Siguiente",
    previous: "Anterior",
    close: "Cerrar",
    description: "Optimizada para SEO con Server-Side Rendering (SSR) y Server Components",
    locale: {
      label: "Idioma de la interfaz",
      hint: "Textos del panel y encabezado Accept-Language hacia la API.",
      es: "Español",
      en: "English",
      fr: "Français",
      pt: "Português",
    },
    theme: {
      label: "Apariencia",
      light: "Claro",
      dark: "Oscuro",
      system: "Sistema",
    },
  },
  auth: {
    login: "Iniciar sesión",
    logout: "Cerrar sesión",
    register: "Registrarse",
    email: "Correo electrónico",
    password: "Contraseña",
    forgotPassword: "¿Olvidaste tu contraseña?",
    resetPassword: "Restablecer contraseña",
    rememberMe: "Recordarme",
    welcome: "Bienvenido",
    welcomeBack: "Bienvenido de nuevo",
  },
  email: {
    welcome: {
      subject: "¡Bienvenido a nuestra plataforma!",
      title: "¡Hola {{name}}!",
      content: "Gracias por unirte a nuestra plataforma. Estamos emocionados de tenerte aquí.",
      buttonText: "Comenzar",
    },
    verification: {
      subject: "Verifica tu correo electrónico",
      title: "Verifica tu correo electrónico",
      content: "Haz clic en el siguiente enlace para verificar tu dirección de correo electrónico:",
      buttonText: "Verificar correo",
      expiresIn: "Este enlace expirará en 24 horas.",
    },
    passwordReset: {
      subject: "Recupera tu contraseña",
      title: "Recuperación de contraseña",
      content: "Haz clic en el siguiente enlace para restablecer tu contraseña:",
      buttonText: "Restablecer contraseña",
      expiresIn: "Este enlace expirará en 1 hora.",
      ignore: "Si no solicitaste este cambio, ignora este correo.",
    },
    houseInvitation: {
      subject: "Te han invitado a un hogar en HomeExpense",
      title: "Invitación al hogar",
      greeting: "{{inviterName}} te ha invitado a unirte a «{{houseName}}» en HomeExpense.",
      content:
        "Crea tu cuenta o inicia sesión con este correo para aceptar la invitación y empezar a compartir gastos.",
      buttonRegister: "Crear cuenta y unirme",
      loginHint: "¿Ya tienes cuenta?",
      loginCta: "Inicia sesión con este enlace",
      expiresIn: "Esta invitación caduca en 14 días.",
      footer: "Si no esperabas este correo, puedes ignorarlo.",
    },
  },
  errors: {
    generic: "Ha ocurrido un error",
    notFound: "No encontrado",
    unauthorized: "No autorizado",
    forbidden: "Acceso prohibido",
    validation: "Error de validación",
    serverError: "Error del servidor",
    networkError: "Error de conexión",
  },
  validation: {
    required: "Este campo es obligatorio",
    email: "Correo electrónico inválido",
    minLength: "Debe tener al menos {{min}} caracteres",
    maxLength: "No puede tener más de {{max}} caracteres",
    passwordMismatch: "Las contraseñas no coinciden",
  },
  dashboardUi: {
    shell: {
      brandTitle: "HomeExpense",
      brandTagline: "Gestión clara de gastos del hogar",
      brandSlogan: "Construyendo el futuro de tu familia,\nde un gasto a la vez.",
      addExpense: "Añadir gasto",
      searchPlaceholder: "Buscar en HomeExpense…",
      userStubName: "Usuario",
      userStubRole: "Miembro",
      notificationsComingSoon: "Notificaciones (próximamente)",
      noActiveHouse: "Sin casa seleccionada",
      openingBalanceLine: "Saldo inicial: {{amount}}",
    },
    nav: {
      mainNavigation: "Navegación principal",
      dashboard: "Panel",
      journal: "Diario",
      search: "Buscar",
      budget: "Presupuesto",
      analytics: "Tendencias",
      vault: "Bóveda",
      scan: "Escanear IA",
      financings: "Financiaciones",
      houses: "Casas",
      profile: "Perfil",
      accountData: "Cuenta y datos",
      settings: "Ajustes",
      support: "Soporte",
    },
    placeholder: {
      hint: "Pantalla en construcción (fase de maquetación). La API sigue el contrato de docs/REQUISITOS_HOMEEXPENSE.md §14.",
    },
    pages: {
      home: "Inicio del panel",
      login: "Iniciar sesión",
      register: "Registro",
      forgotPassword: "Recuperar contraseña",
      resetPassword: "Restablecer contraseña",
      settingsPassword: "Cambiar contraseña",
      settingsPasskeys: "Passkeys",
      settingsProfile: "Perfil de usuario",
      settingsPersonal: "Datos personales",
      settings: "Ajustes",
      support: "Soporte",
      houses: "Mis casas",
      houseMembers: "Miembros de la casa",
      expenses: "Diario de gastos",
      financings: "Financiaciones",
      financingNew: "Nueva financiación",
      financingDetail: "Detalle de financiación",
      financingEdit: "Editar financiación",
      expenseNew: "Nuevo gasto",
      expenseDetail: "Detalle del gasto",
      expensePaidFeedback: "Gasto pagado",
      budget: "Presupuesto mensual",
      vault: "Bóveda de garantías",
      vaultNew: "Registrar activo",
      vaultDetail: "Detalle del activo",
      scan: "Escaneo con IA",
      houseAnalytics: "Tendencias del hogar",
      search: "Buscar",
      notifications: "Notificaciones",
      notificationSettings: "Ajustes de notificaciones",
      settingsDevices: "Dispositivos y sesiones",
      onboarding: "Configuración inicial",
    },
    searchPage: {
      subtitle: "Encuentra gastos y activos de la bóveda en la casa activa.",
      backHome: "Volver al inicio",
      noActiveHouse: "Elige una casa activa para buscar en su diario y bóveda.",
      minCharsHint: "Escribe al menos 2 caracteres para buscar.",
      emptyPrompt: "Escribe en el campo de arriba para buscar por título, notas o categoría de gastos, o por nombre y notas en la bóveda.",
      resultsFor: "Resultados para «{{query}}»",
      openJournalWithQuery: "Ver todo en el diario",
      noResults: "No hay coincidencias. Prueba con otras palabras o revisa el diario con filtros.",
      sectionExpenses: "Gastos",
      sectionVault: "Bóveda",
    },
    states: {
      loadingHint: "Cargando datos…",
      emptyTitle: "No hay nada aquí todavía",
      emptyHint: "Cuando haya datos, aparecerán en esta vista.",
      errorTitle: "Algo salió mal",
      errorHint: "Vuelve a intentarlo o comprueba tu conexión.",
      retry: "Reintentar",
    },
    mobile: {
      openMenu: "Abrir menú",
      homeLink: "Inicio",
    },
    analytics: {
      openFromHome: "Ver tendencias de gasto (últimos meses)",
      openFromBudget: "Abrir tendencias y gráficas del hogar",
      backToBudget: "Volver al presupuesto",
      missingHouse: "Elige una casa para ver la analítica.",
      subtitle:
        "Gasto pagado frente al plan por mes, categorías y pistas de presupuesto (últimos {{count}} meses).",
      empty:
        "Aún no hay gastos pagados en este periodo. Cuando registres gastos, verás gráficas e ideas aquí.",
      chartTitle: "Pagado frente al plan por mes",
      chartPaid: "Pagado",
      chartPlanned: "Planificado",
      chartAvgLine: "Media de pagado (meses con gasto)",
      peakMonthTitle: "Mes con más gasto",
      peakMonthBody: "{{month}} — {{amount}} pagados",
      peakMonthNone: "No hay gastos registrados en esta ventana.",
      avgSpendTitle: "Media pagada por mes con actividad",
      avgSpendHint: "Solo se incluyen meses con gasto pagado mayor que cero.",
      topCategoriesTitle:
        "Categorías con más gasto acumulado (últimos {{count}} meses)",
      otherCategories: "Resto de categorías sumadas: {{amount}}",
      overspendLeaderTitle:
        "Mayor desviación acumulada sobre el plan (últimos {{count}} meses)",
      overspendLeaderBody:
        "«{{name}}» acumula {{amount}} por encima del plan en los meses con partida de presupuesto, sumando solo los últimos {{count}} meses.",
      reviewTitle: "Categorías a revisar",
      reviewHint:
        "A menudo por encima del plan cuando había partida — sube el plan o reduce el gasto.",
      reviewEmpty: "Ninguna categoría encaja con este patrón en el periodo seleccionado.",
      reduceTitle: "Margen para bajar el plan",
      reduceHint: "A menudo por debajo del plan — puedes reducir la asignación mensual con calma.",
      reduceEmpty: "Ninguna categoría encaja con este patrón en el periodo seleccionado.",
    },
    onboarding: {
      pageTitle: "Tu espacio en tres pasos",
      progress: "Paso {{current}} de {{total}}",
      stepWelcomeTitle: "Bienvenido a HomeExpense",
      stepWelcomeBody:
        "Te guiaremos para crear o confirmar tu hogar, invitar a quien comparte gastos y fijar un tope mensual de referencia. Puedes saltarte el tour en cualquier momento.",
      stepHouseTitle: "Nombre del hogar",
      stepHouseBody: "Es el espacio donde agruparéis gastos, presupuesto y miembros.",
      stepHouseHasHouseTitle: "Tu hogar",
      stepHouseHasHouseBody: "Ya formas parte de «{{name}}». Puedes continuar o crear otro hogar desde «Casas» cuando quieras.",
      stepMembersTitle: "Quién comparte el hogar",
      stepMembersBody:
        "Invita por correo a tu pareja, compañeros de piso u otros responsables. También puedes continuar solo y enviar invitaciones luego.",
      inviteEmailLabel: "Correo a invitar",
      inviteEmailPlaceholder: "nombre@ejemplo.com",
      inviteSend: "Enviar invitación",
      inviteSending: "Enviando…",
      inviteSent: "Invitación enviada.",
      inviteSkipHint: "Puedes invitar más tarde desde Miembros de la casa.",
      stepBudgetTitle: "Tope mensual (referencia)",
      stepBudgetBody:
        "Un límite orientativo para el mes ayuda al panel a mostrarte el ritmo de gasto. Lo podrás cambiar en Presupuesto.",
      budgetAmountLabel: "Importe mensual ({{currency}})",
      budgetSkipHint: "Puedes dejarlo vacío y configurarlo después.",
      stepDoneTitle: "Listo",
      stepDoneBody: "Ya puedes usar el panel. ¿Quieres un recorrido rápido por la interfaz?",
      startTour: "Ver tour del panel",
      goHome: "Ir al inicio",
      skipAll: "Omitir y entrar al panel",
      next: "Siguiente",
      back: "Atrás",
      creatingHouse: "Creando…",
      savingBudget: "Guardando…",
      finishing: "Finalizando…",
      houseNameLabel: "Nombre del hogar",
      houseNamePlaceholder: "Ej. Calle Mayor, Familia García…",
      invalidAmount: "Introduce un importe válido (número mayor que cero).",
    },
    productTour: {
      sidebarHomeTitle: "Panel",
      sidebarHomeDesc: "Desde aquí vuelves al resumen del hogar activo: saldo, gasto del mes y accesos rápidos.",
      navHousesTitle: "Casas",
      navHousesDesc:
        "Crea un hogar, elige el activo y entra a miembros o ajustes por casa. Sin hogar no hay gastos ni presupuesto asociados.",
      navJournalTitle: "Diario",
      navJournalDesc:
        "Listado de gastos del hogar activo: puntual y recurrente, filtros y detalle. Si aún no tienes casa, este enlace te lleva a Casas.",
      navBudgetTitle: "Presupuesto",
      navBudgetDesc:
        "Tope mensual, reparto por categorías y seguimiento frente al gasto real. Configúralo cuando tengas el hogar listo.",
      homeMembersTitle: "Miembros del hogar",
      homeMembersDesc:
        "Desde aquí abres invitaciones, roles y reparto por defecto. También lo encuentras en Casas → tu hogar → Miembros.",
      sidebarAddExpenseTitle: "Nuevo gasto",
      sidebarAddExpenseDesc: "Registra un gasto puntual o abre el formulario para añadirlo al diario.",
      sidebarAddExpenseClosingDesc:
        "Desde aquí registras gastos en cualquier momento. El tour termina: ya sabes crear la casa, invitar, elegir si el gasto es del hogar (se reparte) o personal, indicar quién pagó y planificar el presupuesto.",
      housesCreateTourTitle: "Crear tu casa",
      housesCreateTourDesc:
        "Escribe un nombre y pulsa crear: es el contenedor de gastos, miembros y presupuesto. Puedes tener varias y activar la que uses en el día a día.",
      housesEmptyTourTitle: "Sin casas todavía",
      housesEmptyTourDesc:
        "Cuando crees la primera aparecerá aquí el resumen y podrás abrir Miembros y Presupuesto. Vuelve a este tour desde Ajustes cuando quieras.",
      housesMembersLinkTourTitle: "Miembros de la casa",
      housesMembersLinkTourDesc:
        "Desde Miembros invitas por correo, defines roles y el reparto por defecto de cada gasto. Hazlo antes de compartir muchos gastos.",
      membersInviteTourTitle: "Invitar al hogar",
      membersInviteTourDesc:
        "Indica el correo y el rol (miembro o administrador). La persona recibirá enlace para unirse; las invitaciones pendientes las ves más abajo.",
      membersSplitTourTitle: "Reparto entre miembros",
      membersSplitTourDesc:
        "Los porcentajes suman el 100 % y marcan cuánto corresponde a cada uno en gastos compartidos. Solo administradores y propietarios pueden guardar cambios.",
      budgetHeroTourTitle: "Presupuesto por mes",
      budgetHeroTourDesc:
        "Elige el mes con las flechas o el calendario. Arriba ves el total planificado y la comparación con el mes anterior si hay datos.",
      budgetGoalTourTitle: "Objetivo y barra de avance",
      budgetGoalTourDesc:
        "La barra compara lo ya pagado del mes con el tope o con la suma planificada por categorías. Si hay tope mensual, también se muestra como referencia.",
      homeQuickActionsTitle: "Acciones rápidas",
      homeQuickActionsDesc: "Atajos para registrar gasto, escanear ticket o gestionar miembros sin perder el contexto.",
      settingsReplay: "Ver tour del panel otra vez",
      settingsReplayHint: "Abre el inicio con el recorrido guiado por la interfaz.",
      fallbackTitle: "Tu panel",
      fallbackBody:
        "Usa el menú lateral (o el móvil): Casas para dar de alta el hogar, Diario y Presupuesto para operar, y desde el inicio los accesos rápidos a miembros y gastos.",
    },
    auth: {
      appName: "HomeExpense",
      loginTitle: "Bienvenido de nuevo",
      loginSubtitle: "Introduce tus credenciales para acceder a tu diario financiero.",
      registerTitle: "Crea tu espacio",
      registerSubtitle: "Regístrate para gestionar gastos del hogar con calma editorial.",
      forgotTitle: "Recuperar acceso",
      forgotSubtitle: "Indica tu correo y te enviaremos un enlace para restablecer la contraseña.",
      resetTitle: "Nueva contraseña",
      resetSubtitle: "Elige una contraseña segura para tu cuenta.",
      name: "Nombre",
      namePlaceholder: "Tu nombre",
      emailLabel: "Correo electrónico",
      emailPlaceholder: "nombre@ejemplo.com",
      passwordLabel: "Contraseña",
      passwordPlaceholder: "••••••••",
      confirmPasswordLabel: "Confirmar contraseña",
      signInCta: "Entrar",
      signUpCta: "Crear cuenta",
      sendResetLink: "Enviar enlace",
      saveNewPassword: "Guardar contraseña",
      createAccountLink: "Crear tu espacio",
      registerPrompt: "¿No tienes cuenta?",
      alreadyHaveAccount: "¿Ya tienes cuenta?",
      backToLogin: "Volver al inicio de sesión",
      forgotLink: "¿Olvidaste la contraseña?",
      forgotSentHint: "Si existe una cuenta con ese correo, recibirás un enlace en breve.",
      resetSuccess: "Contraseña actualizada. Ya puedes iniciar sesión.",
      invalidResetToken: "El enlace no es válido o ha caducado. Solicita uno nuevo.",
      signingIn: "Entrando…",
      signingUp: "Creando cuenta…",
      inviteLoginBanner: "Inicia sesión con el correo al que llegó la invitación para unirte al hogar.",
      dividerOr: "o",
      continueWithGoogle: "Continuar con Google",
      continueWithApple: "Continuar con Apple",
      continueWithPasskey: "Continuar con Passkey",
      signUpWithPasskey: "Registrarse con Passkey",
      registerWithPasskeyAfterSignUp: "Registrar passkey después de crear la cuenta",
      continuingWithGoogle: "Conectando con Google…",
      continuingWithApple: "Conectando con Apple…",
      continuingWithPasskey: "Conectando con Passkey…",
      passkeyPreferredHint:
        "Si ya registraste una passkey, suele aparecer primero; el navegador puede sugerirla automáticamente al pulsar.",
      errors: {
        generic: "No se pudo completar la acción. Inténtalo de nuevo.",
        invalidEmailOrPassword: "Correo o contraseña incorrectos.",
        userAlreadyExists: "Ya existe una cuenta con ese correo.",
        passwordTooShort: "La contraseña es demasiado corta.",
        passwordTooLong: "La contraseña es demasiado larga.",
        invalidEmail: "El correo no es válido.",
        invalidToken: "El enlace no es válido o ha caducado.",
        resetDisabled: "La recuperación de contraseña no está disponible.",
        invalidOrigin: "Origen no permitido. Revisa CORS y VITE_APP_URL.",
        network: "Error de red. Comprueba la conexión y la URL de la API.",
        emailNotVerified: "Debes verificar tu correo antes de entrar.",
        invalidPassword: "La contraseña actual no es correcta.",
        credentialAccountNotFound: "Esta cuenta no usa contraseña (p. ej. solo enlace social). Usa recuperar acceso o contacta con soporte.",
        providerNotConfigured: "El inicio de sesión con Google no está disponible. Revisa la configuración del servidor.",
      },
    },
    houses: {
      pageTitle: "Mis casas",
      createTitle: "Nueva casa",
      nameLabel: "Nombre",
      namePlaceholder: "Ej. Casa principal",
      createCta: "Crear casa",
      creating: "Creando…",
      yourRole: "Tu rol",
      membersLink: "Miembros",
      setActive: "Usar esta casa",
      emptyTitle: "Aún no tienes casas",
      emptyHint: "Crea una casa para empezar a registrar gastos y compartirlos con tu hogar.",
      cardSummaryPeriod: "Resumen {{yearMonth}}",
      cardPlannedShort: "Previsto",
      cardSpentShort: "Pagado",
      cardUsageShort: "Uso",
      cardStatusWithin: "Dentro del presupuesto",
      cardStatusTight: "Cerca del límite",
      cardStatusOver: "Por encima del presupuesto",
      cardStatusNeutral: "Sin tope ni plan del mes",
      cardSummaryError: "No se pudo cargar el resumen",
      openingBalanceLabel: "Saldo inicial (cuenta)",
      openingBalanceEdit: "Editar saldo inicial",
      openingBalanceSave: "Guardar",
      openingBalanceCancel: "Cancelar",
      openingBalanceSaving: "Guardando…",
      openingBalanceAmountLabel: "Importe",
      openingBalanceHint: "Referencia del saldo bancario al configurar la casa (menor unidad: céntimos). Visible en el panel y la cabecera.",
      openingBalanceReadOnlyHint: "Solo administradores o propietarios pueden cambiar el saldo inicial.",
      openingBalanceInvalidAmount: "Introduce un importe válido (0 o mayor).",
      openingBalanceLoadError: "No se pudo cargar el saldo inicial.",
      financialProfileSectionTitle: "Perfil financiero",
      financialProfileSubtitle: "Saldo inicial de referencia, moneda del hogar y tope mensual opcional.",
      financialProfileEdit: "Editar perfil financiero",
      financialProfileFinancingsLink: "Ver financiaciones",
      currencyLabel: "Moneda (ISO 4217)",
      currencyPlaceholder: "Código de tres letras, p. ej. EUR",
      monthlyCapLabel: "Tope mensual (opcional)",
      monthlyCapPlaceholder: "Ej. 2500,50",
      monthlyCapHint: "Límite de referencia para el hogar; déjalo vacío para quitar el tope.",
      monthlyCapNone: "Sin tope mensual",
      financialProfileReadOnlyHint: "Solo administradores o propietarios pueden editar el perfil financiero.",
      financialProfileInvalidCurrency: "La moneda debe ser un código ISO de exactamente 3 letras (p. ej. EUR).",
      monthlyCapInvalidAmount: "Introduce un importe válido para el tope o déjalo vacío.",
      financialProfileSaveSuccess: "Perfil financiero guardado.",
      financialProfileLoadError: "No se pudo cargar el perfil financiero.",
      setActiveSuccess: "Casa activa actualizada.",
      editNameCta: "Editar nombre",
      editNameSave: "Guardar nombre",
      editNameCancel: "Cancelar",
      deleteCta: "Eliminar casa",
      deleteConfirm:
        "¿Eliminar esta casa? Se borrarán los datos asociados en el sistema. Esta acción no se puede deshacer.",
      updateNameSuccess: "Nombre de la casa actualizado.",
      updateNameError: "No se pudo actualizar el nombre. Inténtalo de nuevo.",
      deleteSuccess: "Casa eliminada.",
      deleteError: "No se pudo eliminar la casa. Inténtalo de nuevo.",
    },
    members: {
      pageTitle: "Miembros",
      roleLabel: "Rol",
      roleAdmin: "Administrador",
      roleMember: "Miembro",
      removeCta: "Quitar",
      removeConfirm: "¿Quitar a este miembro de la casa?",
      columnUser: "Usuario",
      columnRole: "Rol",
      columnSplit: "Reparto (%)",
      backToHouses: "Volver a casas",
      inviteSectionTitle: "Invitar por correo",
      inviteSectionAdminOnly: "Solo propietarios y administradores pueden invitar o gestionar miembros.",
      inviteHelp:
        "Enviaremos un enlace para darse de alta o iniciar sesión. La invitación debe aceptarse con el mismo correo.",
      inviteEmailLabel: "Correo del invitado",
      inviteEmailPlaceholder: "nombre@ejemplo.com",
      inviteSendCta: "Enviar invitación",
      inviteSending: "Enviando…",
      inviteSent: "Invitación enviada. Recibirá un correo con los siguientes pasos.",
      pendingInvitesTitle: "Invitaciones pendientes",
      pendingInviteExpires: "Caduca {{date}}",
      pendingInviteExpiredBadge: "Caducada",
      revokeInviteCta: "Revocar",
      revokeInviteConfirm: "¿Revocar esta invitación?",
      registerInviteHeadline: "Tienes una invitación",
      registerInviteSubline: "Únete a «{{houseName}}» — usa el correo al que se envió la invitación.",
      emailLockedForInvite: "Este correo viene fijado por tu invitación.",
      inviteAcceptWrongEmail: "Inicia sesión con el correo al que se envió la invitación.",
      inviteAcceptedJoined: "Te has unido al hogar.",
      inviteAcceptedAlreadyMember: "Ya eras miembro; la invitación se ha cerrado.",
      removeOwnerNotAllowed: "El propietario no puede eliminarse desde aquí.",
      removeMemberSuccess: "Miembro quitado de la casa.",
      changeRoleSaved: "Rol actualizado.",
      roleOwner: "Propietario",
      splitSectionTitle: "Configuración de reparto",
      splitSectionHelp:
        "Define qué parte del presupuesto mensual corresponde a cada miembro. La suma es siempre 100 %: el último porcentaje se ajusta automáticamente.",
      splitBudgetEyebrow: "Presupuesto mensual",
      splitNoBudgetHint:
        "Sin tope mensual no podemos mostrar importes por persona. Configúralo en la vista de presupuesto.",
      splitOpenBudgetLink: "Ir al presupuesto",
      splitYouBadge: "Tú",
      splitTotalLine: "Total: {{percent}} %",
      splitTotalValid: "Distribución al 100 %",
      splitRemainderHint: "Calculado automáticamente para completar el 100 %.",
      splitSaveCta: "Guardar reparto",
      splitSaving: "Guardando…",
      splitSaveSuccess: "Reparto guardado.",
      splitReadOnlyHint: "Solo administradores y propietarios pueden cambiar el reparto.",
    },
    home: {
      periodLabel: "Periodo {{yearMonth}}",
      spentMonth: "Pagado este mes",
      totalToContributeLabel: "Total a aportar este mes",
      plannedBudget: "Presupuesto planificado",
      budgetUsage: "Uso del presupuesto",
      openingBalance: "Saldo de referencia",
      upcomingTitle: "Próximos gastos",
      upcomingEmpty: "No hay vencimientos próximos.",
      ledgerRecentEmpty:
        "Aún no hay gastos en este hogar. Registra uno en el diario o usa «Nuevo gasto» para verlo aquí.",
      quickActions: "Acciones rápidas",
      actionExpense: "Registrar gasto",
      actionScan: "Escanear ticket",
      actionMembers: "Gestionar miembros",
      insightsStub: "Pronto verás aquí comparativas e ideas para ahorrar.",
      heroOpeningLabel: "Saldo bancario inicial",
      heroProjectedBalanceLabel: "Saldo estimado en cuenta",
      heroProjectedBalanceHint:
        "Base {{opening}} + presupuesto {{need}} − pagado {{spent}} − retenido {{retained}}",
      heroStatSpentLabel: "Pagado del hogar en el periodo",
      heroStatForecastExpensesLabel: "Previsión en gastos del periodo",
      heroStatPlannedLabel: "Presupuesto planificado",
      insightBadge: "Insight inteligente",
      insightTitleWithin: "Vas en buena línea",
      insightTitleTight: "Atención al presupuesto",
      insightTitleOver: "Has superado el presupuesto",
      insightTitleNeutral: "Resumen del periodo",
      insightBodyWithin:
        "Llevas un {{percent}}% del presupuesto planificado para este mes: margen para ajustar sin sobresaltos.",
      insightBodyTight:
        "Ya has consumido gran parte del presupuesto. Revisa el diario antes de nuevos gastos.",
      insightBodyOver:
        "El gasto acumulado supera el presupuesto planificado ({{percent}}%). Conviene revisar categorías.",
      insightBodyNeutral:
        "Configura un tope mensual en el perfil financiero de la casa para ver seguimiento automático.",
      insightCta: "Ver análisis en el diario",
      ledgerEyebrow: "El libro mayor",
      ledgerTitle: "Movimientos recientes",
      ledgerViewAll: "Ver diario completo",
      budgetProgressSubline: "{{spent}} de {{cap}} usados (hogar)",
      gridSpentHint: "Este mes",
      gridForecastHint: "Previsión",
      forecastExpensesMonth: "Comprometido (todos los cargos)",
      gridBudgetHint: "Plan",
      gridUsageHint: "Uso",
      gridSpentSharedLine: "Hogar (repartido)",
      gridSpentPersonalLine: "Personal (solo pagador)",
      gridBudgetMonthlyLabel: "Presupuesto mensual",
      ledgerRowBadge: "Próximo",
      moneySnapshotTitle: "Dinero del mes",
      moneySnapshotOpening: "Saldo inicial",
      moneySnapshotNeedMonth: "Necesito este mes",
      moneySnapshotRetained: "Retenido (prorrateados)",
      moneySnapshotAvailableAfterRetained: "Disponible tras retenido",
      moneySnapshotProjectedBalance: "Saldo estimado en cuenta",
      budgetSetupUrgentTitle: "Aún no hay referencia de presupuesto del mes",
      budgetSetupUrgentBody:
        "Sin líneas por categoría ni tope mensual del hogar, el seguimiento y las alertas pierden sentido. Configúralo en Presupuesto.",
      budgetSetupUrgentCta: "Ir a presupuesto",
      budgetSetupCapOnlyTitle: "Tienes tope mensual: añade categorías",
      budgetSetupCapOnlyBody:
        "El tope orienta el ritmo de gasto. Repartir importes por categoría da un control mucho más claro.",
      budgetSetupCapOnlyCta: "Planificar por categoría",
    },
    expenses: {
      newCta: "Nuevo gasto",
      paid: "Pagado",
      unpaid: "Pendiente",
      paidBy: "Quién pagó",
      scopeShared: "Hogar",
      scopePersonal: "Personal",
      category: "Categoría",
      backToJournal: "Volver al diario",
      backToExpenseDetail: "Volver al gasto",
      ledgerMonthTotalLabel: "Pagado en el mes (solo cargos liquidados)",
      ledgerMonthForecastTotalLabel: "Previsión del mes (todos los cargos)",
      ledgerMemberSharesTitle: "Aportación del pagado (según reparto)",
      ledgerMemberSharesForecastTitle: "Aportación prevista (todos los cargos)",
      ledgerNoSplitsInMonth:
        "Ningún gasto de este mes tiene reparto entre miembros; el total incluye todos los cargos.",
      listForecastVsActual: "Previsión: {{amount}}",
      actionMarkPaid: "Marcar pagado",
      actionSetActualAmount: "Importe real",
      markPaidConfirmTitle: "¿Marcar como pagado?",
      markPaidConfirmBody:
        "El gasto pasará a contar como liquidado para el progreso del mes. Podrás ajustar el importe real después si hace falta.",
      markPaidConfirmCta: "Sí, marcar pagado",
      actionPaidZero: "Pagado a 0 €",
      paidZeroConfirmTitle: "Liquidar a importe 0",
      paidZeroConfirmBody:
        "Se guardará el importe real en 0 € y el gasto quedará como pagado. Sirve para meses en los que ahorras la cuota (p. ej. bimensual): no cuenta como salida de efectivo este mes; la cuota efectiva irá en el mes en que pagues.",
      paidZeroConfirmCta: "Sí, a 0 € y pagado",
      actualAmountModalTitle: "Importe real del gasto",
      actualAmountModalHint:
        "La previsión sigue siendo el importe planificado; el real sirve para comparar con el presupuesto. Si lo dejas vacío, se usa la previsión.",
      actualAmountLabel: "Importe real pagado",
      actualAmountClear: "Quitar importe real",
      actualAmountSubmit: "Guardar",
      paidFeedbackTitle: "Confirmación",
      paidFeedbackHero: "¡Gasto marcado como pagado!",
      paidFeedbackSubline: "Tu seguimiento vuelve a estar al día.",
      paidFeedbackAmountLabel: "Importe contabilizado",
      paidFeedbackCategoryLabel: "Categoría",
      paidFeedbackPaidByLabel: "Pagado por",
      paidFeedbackDateLabel: "Fecha",
      paidFeedbackContribution: "{{percent}} % de aportación",
      paidFeedbackBackHome: "Volver al inicio",
      paidFeedbackViewDetail: "Ver detalle del gasto",
      form: {
        titleLabel: "Concepto",
        titlePlaceholder: "Ej. Supermercado",
        titleOptionalHint: "Si lo dejas vacío y hay categoría, se usará el nombre de la categoría.",
        amountLabel: "Importe total",
        amountHint: "Usa coma o punto para decimales (ej. 24,90).",
        currencyLabel: "Moneda (ISO)",
        dateLabel: "Fecha",
        categoryLabel: "Categoría",
        categoryNone: "Sin categoría",
        paidByLabel: "Quién pagó",
        paidByDefault: "Tú (usuario conectado)",
        paidByJointOption: "Cuenta conjunta",
        paidByHint:
          "Define con qué fondos se registra el gasto (dinero personal, cuenta conjunta del hogar o tú por defecto). No determina si el gasto se reparte: eso lo define «Gasto del hogar».",
        paymentMethodLabel: "Método de pago",
        paymentMethodNone: "Sin especificar",
        paymentMethodCard: "Tarjeta",
        paymentMethodCash: "Efectivo",
        paymentMethodAccount: "Cuenta",
        sharedLabel: "Gasto del hogar (se reparte entre los miembros)",
        sharedHint:
          "Si está activo, el reparto suma el 100 % entre todos los miembros de la casa. Si está desactivado, será un gasto personal (no se reparte).",
        recurringQuestion: "¿Es un gasto recurrente?",
        recurringEndsLabel: "Fecha de fin",
        recurringEndsOptionalHelp:
          "Opcional si no prorrateas: déjala vacía para una serie sin fin (agua, basura, etc.).",
        recurringEndsRequiredWhenProratedHelp:
          "Si la frecuencia es mensual y prorrateas, la fecha de fin es obligatoria (repartir un total entre N cuotas).",
        recurringEndsProrateOpenPeriodHelp:
          "Puedes dejar la fecha en blanco: el importe total es el de cada periodo (p. ej. la factura bimestral) y se crean cargos mensuales que lo reparten en ese periodo.",
        recurringFrequencyLabel: "Frecuencia",
        frequencyMonthly: "Mensual",
        frequencyBimonthly: "Bimestral",
        frequencyQuarterly: "Trimestral",
        frequencySemiannual: "Semestral",
        frequencyAnnual: "Anual",
        proratedLabel: "Prorratear el total entre las cuotas",
        proratedHint: "Si está activo, el importe total se divide entre los pagos según la frecuencia. Si no, cada fecha registra el importe total.",
        recurringSummary: "Cuotas previstas: {{count}}. Importe por periodo: {{installment}}.",
        recurringOpenEndedSummary:
          "Sin fecha de fin: ahora se crean las cuotas hasta unos {{months}} meses vista; cada mes el sistema revisa la serie y amplía el horizonte automáticamente.",
        recurringProratedOpenPeriodPreviewNote:
          "El importe total es por periodo de {{periodMonths}} meses; en el diario verás un cargo cada mes que reparte ese total dentro del periodo.",
        submitRecurring: "Activar gasto recurrente",
        notesLabel: "Notas",
        notesPlaceholder: "Opcional",
        receiptLabel: "Comprobante (opcional)",
        receiptHelp: "PDF o imagen (JPEG, PNG, WebP, GIF). Se sube de forma segura al almacenamiento.",
        receiptRemove: "Quitar archivo",
        paidNowLabel: "Registrar ya como pagado",
        submitCreate: "Guardar gasto",
        submitSave: "Guardar cambios",
        saving: "Guardando…",
        cancelEdit: "Cancelar edición",
        invalidAmount: "Introduce un importe válido (mayor o igual que 0).",
        titleOrCategoryRequired: "Indica un concepto o elige una categoría.",
        recurrenceInvalid: "La fecha de fin debe ser posterior al inicio y debe haber al menos un cargo en el periodo.",
        recurringProratedRequiresEndsOn:
          "Si prorrateas el total, indica la fecha de fin de la serie (no se puede repartir un total sin saber hasta cuándo).",
        recurringPreviewNeedAmount:
          "Indica el importe total arriba para ver el cálculo de las cuotas (modo serie con fecha de fin o prorrateo).",
        recurringPreviewNeedAmountMonthlyFixed:
          "Indica arriba el importe de un solo mes para ver el resumen y el reparto estimado.",
        proratedMonthlyAverageHint: "Media aproximada en el periodo: {{amount}} / mes",
        recurrenceKindLegend: "Tipo de recurrente",
        recurrenceKindMonthlyFixed:
          "Cargo mensual fijo (mismo importe cada mes; no necesitas fecha de fin)",
        recurrenceKindSeries:
          "Serie por frecuencia (mensual, bimestral…); con o sin fecha de fin si no prorrateas",
        seriesSingleChargeHint:
          "Para un único cargo por periodo (anual, trimestral, semestral o bimestral), usa Serie por frecuencia y deja prorrateo desactivado.",
        seriesAnnualPresetAction:
          "Usar preset de suscripción anual (sin prorrateo)",
        amountLabelMonthly: "Importe cada mes",
        amountHintMonthlyFixed:
          "Indica lo que pagas en un solo mes (ej. hipoteca). No es un total a repartir: se repetirá ese importe cada mes.",
        monthlyFixedPatternExplain:
          "No hace falta fecha de fin: en el diario se crean {{count}} cargos, uno por mes, todos con el mismo importe que pongas arriba como «un mes» (ej. cuota de hipoteca). No es un total a repartir entre {{count}} meses: cada mes repite ese importe.",
        monthlyFixedHorizonNote:
          "Verás {{count}} líneas en el diario (una por mes); en cada una se aplicará el reparto que marques abajo.",
        smoothBimonthlyBillHint:
          "Si un recibo llega cada dos meses (p. ej. agua o basura) y quieres reservar la mitad cada mes, usa este modo e indica la mitad del importe bimestral como «un mes».",
        sharedSplitPreviewTitle: "Reparto estimado (este periodo)",
        sharedSplitPreviewSubtitle:
          "Importe que correspondería a cada persona según el reparto del hogar. Si no hay porcentajes en Miembros, se divide a partes iguales.",
        sharedSplitUsesHousePercent: "Usando los % configurados en Miembros",
        sharedSplitEqualParts: "Partes iguales entre miembros",
      },
      detail: {
        edit: "Editar",
        markPaid: "Marcar como pagado",
        markUnpaid: "Marcar como pendiente",
        delete: "Eliminar",
        deleteConfirm: "¿Eliminar este gasto? Esta acción no se puede deshacer.",
        paidAtLabel: "Pagado el",
        notesTitle: "Notas",
        splitsTitle: "Reparto",
        splitsSubtitle:
          "Importe que corresponde a cada persona según los porcentajes guardados en este gasto.",
        splitShare: "{{share}} %",
        notApplicable: "—",
        scopeLabel: "Ámbito",
        paymentMethod: "Método de pago",
        receiptLabel: "Ticket",
        receiptView: "Ver ticket",
        receiptNotAttached: "Sin ticket adjunto",
        receiptReplaceHelp:
          "Puedes subir un nuevo ticket para reemplazar el actual sin salir de esta pantalla.",
        seriesLink: "Ver serie recurrente",
        seriesFutureInstallmentLabel:
          "Importe por cuota (futuras no pagadas se actualizan)",
        seriesSave: "Guardar cambios de la serie",
        seriesLimitSectionTitle: "Acortar o finalizar",
        seriesLimitSectionIntro:
          "Fija la última cuota o finaliza la serie. No uses esta sección si lo que quieres es quitar el límite de fin (abajo).",
        seriesEndsOnLabel: "Última cuota (fecha de fin)",
        seriesEndsOnHelp:
          "Se eliminarán las cuotas futuras no pagadas con fecha posterior a esta. El último cargo incluido es el de esta fecha.",
        seriesApplyEndsOn: "Guardar fecha de fin",
        seriesEndNow: "Finalizar serie ahora",
        seriesEndNowConfirm:
          "Se cancelarán las cuotas futuras no pagadas y la serie dejará de generar cargos. ¿Continuar?",
        seriesExtendSectionTitle: "Ampliar la serie",
        seriesExtendIntroActiveWithEnd:
          "Quita la fecha de fin para que vuelvan a generarse cuotas hasta el horizonte del sistema. En series prorrateadas, las cuotas pasarán a usar el importe fijo por cuota hasta que fijes otra vez un fin.",
        seriesExtendIntroEnded:
          "Reactiva la serie y elimina la fecha de fin. Volverán a generarse cuotas futuras según la recurrencia. No uses esto si solo querías acortar (usa la sección de arriba antes de finalizar).",
        seriesRemoveEndsOn: "Quitar fecha de fin",
        seriesRemoveEndsOnConfirm:
          "¿Quitar la fecha de fin? La serie seguirá activa y se generarán cuotas en el horizonte habitual.",
        seriesReactivateOpenEnded: "Reactivar sin fecha de fin",
        seriesReactivateOpenEndedConfirm:
          "¿Reactivar la serie y quitar la fecha de fin? Se volverán a crear cuotas futuras no pagadas según la recurrencia.",
        seriesReactivateOnly: "Reactivar serie",
        seriesReactivateOnlyConfirm:
          "¿Reactivar la serie? Volverá a estar activa con los datos actuales.",
        seriesExtendIntroEndedNoEnd:
          "La serie está finalizada sin fecha de fin en el registro. Reactívala para volver a generar cuotas en el horizonte habitual.",
        seriesStatusBadgeActive: "Activa",
        seriesStatusBadgePaused: "En pausa",
        seriesStatusBadgeEnded: "Finalizada",
        seriesEndedReadOnly:
          "Serie finalizada. Para volver a generar cuotas, usa la sección «Ampliar la serie».",
        seriesInvalidEndDate: "Indica una fecha válida (AAAA-MM-DD).",
        budgetSetupBannerTitle: "Sin referencia de presupuesto este mes",
        budgetSetupBannerBody:
          "El mes de este gasto no tiene plan por categorías ni tope mensual del hogar. Configúralo en Presupuesto para un seguimiento fiable.",
        budgetSetupBannerCta: "Ir a presupuesto",
      },
      filters: {
        title: "Filtros",
        searchPlaceholder: "Buscar en el diario…",
        scopeLabel: "Ámbito",
        scopeAll: "Todos",
        scopeShared: "Hogar",
        scopePersonal: "Personal",
        categoryAll: "Todas las categorías",
        paidAll: "Todos",
        paidYes: "Solo pagados",
        paidNo: "Solo pendientes",
        apply: "Aplicar",
        clear: "Limpiar",
      },
      flows: {
        launcherTitle: "¿Qué quieres hacer?",
        launcherSubtitle:
          "Elige un recorrido según el tipo de gasto que quieras registrar.",
        manualOnlyBanner:
          "Introduce los datos clave del gasto: importe, fecha y método de pago.",
        intentQuickTitle: "Registrar un gasto puntual",
        intentQuickDescription:
          "Para un cargo que ya hiciste: importe, categoría y cómo pagaste. El resto solo si lo necesitas.",
        intentRecurringTitle: "Gasto fijo cada mes",
        intentRecurringDescription:
          "Una cuota que se repite (alquiler, comunidad…). Te guiamos paso a paso para que sepas qué estás configurando.",
        intentSeriesTitle: "Serie con fechas o prorrateo",
        intentSeriesDescription:
          "Cuotas con fecha de fin, varias frecuencias o reparto del total entre meses. Incluye resumen antes de guardar.",
        quickExpandMore: "Más opciones (concepto, notas, compartido…)",
        repeatLastFlowCta: "Continuar con: {{flowLabel}}",
        navNext: "Siguiente",
        navBack: "Atrás",
        navReview: "Ver resumen",
        editStepA11y: "Editar paso {{step}}",
        monthlyWizard: {
          step1Title: "¿Qué gasto es?",
          step1Help: "Nombre o categoría. Así lo reconocerás en el diario cada mes.",
          step2Title: "¿Cuánto pagas al mes?",
          step2Help: "Un solo importe por mes. Lo repetiremos en cada cuota.",
          step3Title: "¿Cuándo empieza?",
          step3Help: "Primera fecha de cargo o el día en que quieres que aparezca en el diario.",
          step4Title: "¿Cómo lo pagas?",
          step4Help: "Tarjeta, efectivo o cuenta: tú indicas cómo sale el dinero (todo manual).",
          step5Title: "¿Lo compartís en casa?",
          step5Help: "Quién paga y si el gasto es compartido entre miembros.",
          reviewTitle: "Así quedará tu gasto fijo",
          reviewHelp: "Revisa los datos. Puedes volver atrás y cambiar cualquier paso.",
        },
        seriesWizard: {
          step1Title: "Concepto e importe",
          step1Help:
            "Qué es este gasto y el importe según el modo (por mes o total del periodo, según elijas después).",
          step2Title: "Cada cuánto se repite",
          step2Help: "Mensual, trimestral, anual… La frecuencia define cómo aparecen las cuotas en el diario.",
          step3Title: "Desde cuándo y hasta cuándo",
          step3Help:
            "Fecha de inicio y, si aplica, última cuota. Sin fecha de fin, la serie puede seguir abierta según las reglas del formulario.",
          step4Title: "Prorrateo",
          step4Help:
            "Si repartes un total entre varias cuotas, aquí se explica el reparto. Sin banco: confirmas tú los importes.",
          step5Title: "Reparto entre personas",
          step5Help: "Compartido y quién paga. Verás una vista previa cuando el importe y las fechas estén listos.",
          reviewTitle: "Resumen de la serie",
          reviewHelp: "Comprueba cuotas y fechas antes de crear la serie.",
        },
      },
    },
    financings: {
      pageTitle: "Financiaciones",
      newCta: "Nueva financiación",
      emptyTitle: "Sin financiaciones",
      emptyHint:
        "Registra préstamos o cuotas fijas con fecha de inicio de contrato. Las cuotas ya pasadas no se cargan en el diario.",
      stepDataTitle: "Datos",
      stepReviewTitle: "Revisar",
      titleLabel: "Nombre",
      lenderLabel: "Entidad o acreedor",
      installmentLabel: "Cuota mensual",
      contractStartLabel: "Inicio del contrato (calendario de cuotas)",
      endsOnLabel: "Última cuota prevista",
      notesLabel: "Notas",
      docsHint: "Documentación opcional (PDF o imagen). Sube archivos aquí o continúa solo con datos manuales.",
      runAiLabel: "Analizar documentos con IA",
      runAiHint:
        "Resume condiciones importantes de forma orientativa. No es asesoramiento legal ni financiero.",
      generateRecurringLabel: "Generar gasto recurrente mensual",
      generateRecurringHint:
        "Crea una serie en el diario desde la primera cuota en o después de hoy (UTC) hasta la fecha de fin.",
      reviewCutoff: "Fecha de corte: {{date}} (UTC)",
      reviewFirstPayment: "Primera cuota en el diario: {{date}}",
      reviewFutureCount: "Cuotas futuras previstas: {{count}}",
      reviewPreviewDates: "Próximas fechas (muestra)",
      reviewWillSeries: "Se creará una serie de gastos recurrentes",
      reviewWillAi: "Se enviará el primer documento a IA tras guardar",
      reviewNoSeries: "No se crearán gastos automáticos",
      backCta: "Atrás",
      continueCta: "Continuar a revisar",
      confirmCta: "Confirmar y guardar",
      saving: "Guardando…",
      aiDisclaimer: "El resumen por IA es orientativo y puede contener errores.",
      aiSkipped: "IA no aplicada",
      aiPending: "Resumen IA pendiente",
      aiProcessing: "Analizando documentos…",
      aiCompleted: "Resumen IA listo",
      aiFailed: "El resumen IA falló",
      viewSeriesCta: "Ver serie de gastos",
      listInstallment: "Cuota: {{amount}}",
      listTermProgress: "{{percent}}% del plazo",
      listContractEndedBadge: "Finalizado",
      listInstallmentsSummary:
        "{{past}} de {{total}} cuotas vistas · {{remaining}} restantes",
      listEstimatedRemaining: "Pendiente estimado: {{amount}}",
      listNoInstallmentSchedule: "Sin calendario de cuotas",
      categoryLabel: "Categoría",
      categoryNone: "Sin categoría",
      openDocument: "Abrir documento",
      summaryTitle: "Resumen IA",
      keyTermsTitle: "Condiciones destacadas",
      editCta: "Editar",
      deleteCta: "Eliminar",
      deleteConfirm:
        "¿Eliminar esta financiación? Si tenía gastos recurrentes vinculados, la serie se dará por finalizada.",
      saveCta: "Guardar cambios",
      saveSuccess: "Financiación actualizada.",
    },
    budget: {
      heroEyebrow: "Asignación del hogar",
      heroTitle: "Gasto del hogar previsto este mes",
      heroSubtitle:
        "Suma de tus líneas de presupuesto del hogar en este periodo. Ajusta cada categoría abajo y guarda cuando quieras fijar el plan. Las personales se registran por separado en el diario.",
      goalLabel: "Cumplimiento del objetivo: {{yearMonth}}",
      percentComplete: "{{percent}} % completado",
      reservedLabel: "{{amount}} consumido del hogar",
      targetLabel: "{{amount}} objetivo",
      categoryGridTitle: "Por categoría",
      plannedLabel: "Plan del hogar",
      spentLabel: "Gasto del hogar",
      plannedInputHint: "Importe planificado (mayor unidad)",
      monthNav: "Mes",
      prevMonth: "Mes anterior",
      nextMonth: "Mes siguiente",
      saveCta: "Guardar presupuesto",
      saving: "Guardando…",
      saveSuccess: "Presupuesto actualizado.",
      copyFromPreviousConfirmTitle: "¿Copiar desde el mes anterior?",
      copyFromPreviousConfirmMessage:
        "Se aplicarán al mes actual las líneas de presupuesto del mes anterior. Luego podrás ajustarlas.",
      copyFromPreviousCta: "Importar desde el mes anterior",
      readOnlyHint: "Solo administradores y propietarios pueden editar el presupuesto.",
      performanceTitle: "Rendimiento del periodo",
      performanceSubtitle:
        "Comparación entre el plan del hogar y el gasto real del hogar de este mes.",
      legendBudgeted: "Plan del hogar",
      legendSpent: "Gasto del hogar",
      noCategoriesHint:
        "Aún no hay categorías. Crea la primera con la tarjeta «Añadir categoría»; aparecerá al registrar gastos en el diario.",
      addCategoryCard: "Añadir categoría",
      addCategoryCardHint: "Las nuevas categorías se pueden elegir al crear un gasto.",
      addCategoryFormTitle: "Nueva categoría",
      addCategoryNameLabel: "Nombre",
      addCategoryNamePlaceholder: "Ej. Supermercado",
      addCategorySubmit: "Crear",
      addCategoryCancel: "Cancelar",
      addCategoryCreating: "Creando…",
      heroVsPrevUp: "+{{pct}} % respecto al mes anterior",
      heroVsPrevDown: "−{{pct}} % respecto al mes anterior",
      heroVsPrevFlat: "Igual que el mes anterior",
      proratedTitle: "Compromisos prorrateados",
      proratedLinkJournal: "Ver diario",
      proratedReserveHint: "Cuota mensual estimada",
      proratedTotalBadge: "Total {{amount}}",
      proratedEmpty:
        "Aquí verás series con total repartido (IBI, seguros anuales, etc.). Créalas como recurrentes prorrateadas desde el diario.",
      planningSectionTitle: "Planificación por categoría",
      bannerEyebrow: "Hábitos sostenibles",
      bannerTitle: "Un presupuesto visible reduce sustos y alinea el hogar con tus prioridades.",
      badgeOverBudget: "Por encima del plan",
      badgeUnderBudget: "Por debajo del plan",
      badgeOnTrack: "En línea",
      badgeNoPlan: "Sin plan",
      columnBudget: "Plan",
      columnReal: "Real",
      analysisEmpty:
        "Aún no hay categorías con plan o gasto este mes. Define importes abajo o registra movimientos en el diario.",
      monthlyCapHint: "Tope mensual del hogar",
      moneySnapshotTitle: "Resumen rápido de caja",
      moneySnapshotOpening: "Saldo inicial",
      moneySnapshotNeedMonth: "Necesito este mes",
      moneySnapshotRetained: "Retenido (prorrateados)",
      moneySnapshotAvailableAfterRetained: "Disponible tras retenido",
      moneySnapshotProjectedBalance: "Saldo estimado en cuenta",
    },
    vault: {
      pageTitle: "Bóveda",
      pageSubtitle:
        "Gestiona garantías y pólizas del hogar con seguridad. Te avisaremos antes de que venza la cobertura.",
      emptyHint: "Registra garantías, pólizas y activos con sus fechas clave.",
      addCta: "Registrar activo",
      nameLabel: "Nombre del artículo",
      namePlaceholder: "p. ej., Samsung Smart Fridge",
      assetTypeLabel: "Categoría",
      assetTypePlaceholder: "Ej. Póliza, electrodoméstico",
      purchaseDateLabel: "Fecha de compra",
      warrantyLabel: "Fin de garantía",
      notesLabel: "Notas",
      notesPlaceholder: "Detalles del contrato, referencias…",
      notesOptionalHint: "Opcional",
      documentKeyLabel: "Clave de documento (storage)",
      documentKeyPlaceholder: "Tras subir un archivo a almacenamiento",
      saveCta: "Guardar",
      saving: "Guardando…",
      createCta: "Guardar en la bóveda",
      backToList: "Volver a la bóveda",
      deleteCta: "Eliminar",
      deleteConfirm: "¿Eliminar este ítem de la bóveda?",
      readOnlyHint: "Solo administradores y propietarios pueden crear o editar ítems.",
      cardWarranty: "Garantía hasta {{date}}",
      cardPurchased: "Comprado {{date}}",
      formKicker: "Registro",
      formTitle: "Nuevo activo en la bóveda",
      formSubtitle: "Asegura tu inversión con la información original de compra y el comprobante.",
      priceLabel: "Precio / valor",
      pricePlaceholder: "0,00",
      priceInNotesLine: "Valor estimado: {{amount}}",
      documentUploadTitle: "Ticket o factura",
      documentUploadHint: "Sube el comprobante de compra",
      documentUploadSub: "Toca para elegir archivo o imagen",
      documentUploadedHint: "Documento adjunto listo",
      completionLabel: "Progreso del registro",
      saveLegalNote:
        "Al guardar, autorizas el almacenamiento cifrado de tus documentos y fechas de garantía en la nube.",
      filterAll: "Todos",
      filterEmpty: "No hay ítems en esta categoría.",
      alertKicker: "Alerta",
      alertTitle: "Atención a la garantía",
      alertDaysLeft: "La garantía de «{{name}}» vence en {{days}} días.",
      alertExpired: "La garantía de «{{name}}» ha vencido. Revisa la cobertura.",
      alertViewDetails: "Ver detalle",
      statTotalLabel: "Activos en bóveda",
      statSecuredLabel: "Con fecha de garantía",
      statSecuredLine: "Ítems con fecha de garantía vigente o futura",
      sectionCount: "{{count}} ítems",
      cardPurchaseDate: "Fecha de compra",
      cardExpiryDate: "Fin de garantía",
      viewDocument: "Ver comprobante",
      registerNewItem: "Registrar nuevo ítem",
      warrantyBadgeExpired: "Vencida",
      warrantyBadgeUrgent: "Urgente",
      warrantyBadgeWarning: "Próxima",
      warrantyBadgeSafe: "En orden",
      warrantyBadgeNone: "Sin fecha",
      warrantyEndsToday: "Vence hoy",
      categoryPlaceholder: "Elige categoría",
      sectionUncategorized: "Sin categoría",
      categories: {
        appliances: "Electrodomésticos",
        electronics: "Electrónica",
        furniture: "Muebles",
        vehicles: "Vehículos",
        other: "Otros",
      },
    },
    scan: {
      overlayTitle: "Escaneo con IA",
      overlaySubtitle: "Sube una foto del ticket; el procesamiento puede tardar unos instantes.",
      pickFile: "Elegir imagen",
      uploading: "Subiendo…",
      registering: "Registrando escaneo…",
      statusPending: "En cola",
      statusProcessing: "Procesando",
      statusCompleted: "Completado",
      statusFailed: "Error",
      extractedTitle: "Datos detectados",
      rawPayloadHint: "Vista previa del resultado (JSON).",
      recentTitle: "Escaneos recientes",
      linkExpenseHint: "Revisa los datos y crea el gasto en el diario cuando el escaneo esté completado.",
      pollHint: "Actualizando estado…",
      createExpenseFromScan: "Crear gasto con estos datos",
      fromScanBanner: "Datos precargados desde un escaneo con IA. Revísalos antes de guardar.",
      extractionErrorFallback: "No se pudo interpretar el ticket.",
      extractedMerchant: "Comercio",
      extractedDate: "Fecha",
      extractedTotal: "Total",
    },
    profile: {
      pageTitle: "Perfil",
      accountSettings: "Cuenta y datos",
      accountSettingsHint: "Preferencias personales",
      notifications: "Notificaciones",
      notificationsHint: "Push, preferencias y dispositivos",
      devices: "Dispositivos y sesiones",
      devicesHint: "Ver dónde iniciaste sesión y cerrar sesión remota",
      privacy: "Privacidad y seguridad",
      privacyHint: "Contraseña y permisos",
      help: "Ayuda y soporte",
      helpHint: "Preguntas frecuentes y contacto",
      logout: "Cerrar sesión",
      logoutHint: "Salir de forma segura",
      footerNote: "HomeExpense",
      profileFormSectionTitle: "Perfil e idioma",
      nameField: "Nombre visible",
      saveButton: "Guardar cambios",
      saving: "Guardando…",
      savedHint: "Cambios guardados.",
      clearAvatarButton: "Quitar imagen",
      avatarChangeAriaLabel: "Cambiar foto de perfil",
      avatarUploading: "Subiendo imagen…",
      avatarUploadError: "No se pudo subir la imagen. Prueba con JPG, PNG o WebP (máx. 5 MB).",
    },
    personalDataPage: {
      subtitle: "Correo, idioma de la interfaz y nombre visible.",
      emailLabel: "Correo electrónico",
      emailHint: "Correo vinculado a tu sesión. No se puede editar aquí.",
      deleteAccountSectionTitle: "Eliminar cuenta",
      deleteAccountSectionHint:
        "Se borrarán de forma permanente tu usuario, sesiones y datos personales asociados. En hogares compartidos, otra persona pasará a figurar como creadora de los registros que queden.",
      deleteAccountButton: "Eliminar mi cuenta y mis datos",
      deleteAccountModalTitle: "¿Eliminar cuenta permanentemente?",
      deleteAccountModalBody:
        "Esta acción no se puede deshacer. Perderás el acceso a HomeExpense y se eliminarán tus datos de cuenta. Si compartías un hogar, el resto de miembros conservará el hogar y el historial reasignado.",
      deleteAccountModalConfirm: "Sí, eliminar mi cuenta",
      deleteAccountModalCancel: "Cancelar",
      deleteAccountError: "No se pudo completar la eliminación. Inténtalo de nuevo o contacta con soporte.",
      deleteAccountPending: "Eliminando cuenta…",
    },
    settingsHub: {
      unifiedPageTitle: "Cuenta y datos",
      unifiedSubtitle: "Foto de perfil y accesos a datos personales, seguridad y ayuda.",
      backToAccount: "Volver a cuenta y datos",
      cardPersonalDataTitle: "Datos personales",
      cardPersonalDataHint: "Idioma, nombre visible y correo",
      cardPasswordTitle: "Contraseña",
      cardPasswordHint: "Cambiar contraseña con la sesión iniciada",
      cardPasskeysTitle: "Passkeys",
      cardPasskeysHint: "Añade o gestiona acceso sin contraseña en este navegador",
      cardNotificationsTitle: "Notificaciones",
      cardNotificationsHint: "Push en el dispositivo y tipos de alerta",
      cardDevicesTitle: "Dispositivos y sesiones",
      cardDevicesHint: "Sesiones activas y cierre remoto",
      cardSupportTitle: "Ayuda y soporte",
      cardSupportHint: "Recuperar acceso y preguntas frecuentes",
      cardDeleteAccountTitle: "Eliminar cuenta",
      cardDeleteAccountHint: "Borrar permanentemente tu usuario y datos asociados",
    },
    settingsPasswordForm: {
      subtitle: "Introduce tu contraseña actual y la nueva. Si no recuerdas la actual, usa recuperar acceso.",
      currentPassword: "Contraseña actual",
      newPassword: "Nueva contraseña",
      confirmPassword: "Confirmar nueva contraseña",
      submit: "Actualizar contraseña",
      successMessage: "Contraseña actualizada correctamente.",
      forgotPasswordCta: "No recuerdo mi contraseña",
      noCredentialHint: "No hay contraseña asociada a esta cuenta. Puedes definir una desde recuperar acceso o usar tu proveedor de inicio de sesión.",
      changeAgain: "Cambiar de nuevo",
    },
    settingsPasskeysPage: {
      subtitle: "Registra una passkey en este dispositivo para iniciar sesión más rápido la próxima vez.",
      disabledHint: "Las passkeys están desactivadas en este entorno (VITE_PASSKEY_AUTH_ENABLED).",
      addHint: "Usa un autenticador de plataforma (p. ej. Touch ID, Windows Hello o clave del sistema).",
      addButton: "Añadir passkey",
      adding: "Registrando…",
      loading: "Cargando passkeys…",
      loadError: "No se pudieron cargar las passkeys. Inténtalo de nuevo.",
      empty: "Aún no tienes passkeys registradas en esta cuenta.",
      unnamedPasskey: "Passkey sin nombre",
      deviceTypeLabel: "Tipo: {{type}}",
      createdLabel: "Alta:",
      renameButton: "Renombrar",
      renameDialogTitle: "Nombre de la passkey",
      renameLabel: "Nombre visible",
      renameSave: "Guardar",
      renameEmpty: "Indica un nombre para la passkey.",
      deleteButton: "Eliminar",
      deleteConfirm: "¿Eliminar esta passkey? No podrás usarla para iniciar sesión.",
      deleting: "Eliminando…",
    },
    supportPage: {
      subtitle: "Recursos y enlaces útiles.",
      intro: "Aquí encontrarás ayuda para acceder a tu cuenta y enlaces relacionados con la seguridad.",
      forgotTitle: "¿Olvidaste tu contraseña?",
      forgotBody: "Te enviamos un enlace por correo para restablecerla sin necesidad de conocer la anterior.",
      forgotCta: "Ir a recuperar acceso",
    },
    notificationsPage: {
      subtitle: "Alertas y avisos de tu hogar.",
      emptyTitle: "No hay notificaciones",
      emptyHint: "Cuando haya novedades (invitaciones, recordatorios, etc.), aparecerán aquí.",
      inboxTitle: "Bandeja",
      pushSectionTitle: "Notificaciones push",
      activatePush: "Activar en este navegador",
      pushWorking: "Push activo en este dispositivo.",
      permissionDenied: "Permisos de notificación denegados. Actívalos en la configuración del navegador.",
      notSupported: "Este navegador no admite notificaciones push.",
      configMissing: "Falta configuración de Firebase en el entorno (variables VITE_FIREBASE_*).",
      registerError: "No se pudo registrar el dispositivo para push.",
      devicesSectionTitle: "Dispositivos registrados",
      noDevices: "Aún no hay dispositivos. Activa push arriba para registrar este navegador.",
      platformLabel: "Plataforma",
      platformWeb: "Web",
      platformIos: "iOS",
      platformAndroid: "Android",
      lastSeenLabel: "Última actividad",
      removeDevice: "Quitar dispositivo",
      prefsSectionTitle: "Preferencias de avisos",
      prefPushMaster: "Recibir notificaciones push",
      prefHouseInvites: "Hogar e invitaciones",
      prefExpenses: "Gastos y diario",
      prefBudget: "Presupuesto y límites",
      prefReminders: "Recordatorios",
      prefScanDocs: "Escaneos y documentos",
      prefDigests: "Resúmenes (digest)",
      prefUpcomingCharges: "Cargos de mañana (no pagados)",
      markAllRead: "Marcar todo como leído",
      markOneRead: "Marcar como leída",
      loadMore: "Cargar más",
      unreadLabel: "Sin leer",
      viewAction: "Abrir",
      removeDeviceConfirm: "¿Quitar este dispositivo de las notificaciones push?",
      devicePushLabel: "Push en este dispositivo",
    },
    pendingInvitationsPage: {
      title: "Invitaciones pendientes",
      subtitle:
        "Aquí puedes aceptar invitaciones a hogares. Hasta que aceptes, no tendrás acceso a la casa ni a la gestión de miembros.",
      emptyTitle: "No hay invitaciones pendientes",
      emptyHint:
        "Si te invitan por correo, revisa también el enlace del email. Las notificaciones de la app te traerán aquí cuando haya una invitación activa.",
      inviterLine: "Te invita: {{inviterName}}",
      expiresLabel: "Caduca el {{date}}",
      acceptCta: "Aceptar e unirme",
      accepting: "Uniendo…",
      loadError: "No se pudieron cargar las invitaciones. Inténtalo de nuevo.",
    },
    notificationSettingsPage: {
      subtitle: "Activa notificaciones en el navegador, elige qué avisos recibir y gestiona dispositivos.",
    },
    notificationsInApp: {
      pushDeviceRegisteredTitle: "Dispositivo para notificaciones",
      pushDeviceRegisteredBody: "Se ha registrado correctamente este dispositivo ({{platform}}).",
      receiptScanCompletedTitle: "Ticket listo para revisar",
      receiptScanCompletedBody:
        "El escaneo en {{houseName}} ha terminado. Puedes crear el gasto con los datos detectados.",
      receiptScanFailedTitle: "No se pudo leer el ticket",
      receiptScanFailedBody:
        "El escaneo en {{houseName}} ha fallado. Prueba con otra imagen o revisa el estado en Escaneo IA.",
      houseInvitationReceivedTitle: "Invitación a una casa",
      houseInvitationReceivedBody:
        "{{inviterName}} te ha invitado a unirte a «{{houseName}}». Abre el enlace para aceptar la invitación.",
      houseInvitationAcceptedTitle: "Alguien se ha unido a la casa",
      houseInvitationAcceptedBody: "{{inviteeName}} ha aceptado la invitación y ya forma parte de «{{houseName}}».",
      budgetThreshold80Title: "Presupuesto al {{percent}}%",
      budgetThreshold80Body:
        "En «{{houseName}}» el gasto pagado del mes {{yearMonth}} ha alcanzado el {{percent}}% del objetivo.",
      budgetThreshold100Title: "Presupuesto al límite",
      budgetThreshold100Body:
        "En «{{houseName}}» el gasto pagado del mes {{yearMonth}} ha llegado al {{percent}}% del objetivo.",
      budgetMonthStartedTitle: "Nuevo mes en {{houseName}}",
      budgetMonthStartedBody: "Empieza {{yearMonth}}. Revisa el presupuesto y los objetivos del hogar.",
      budgetMonthNeedsSetupTitle: "Presupuesto pendiente en {{houseName}}",
      budgetMonthNeedsSetupBody:
        "Empieza {{yearMonth}} y no había un plan anterior que copiar. Define líneas por categoría en Presupuesto o fija un tope mensual del hogar como respaldo.",
      budgetMonthSeededFromPreviousTitle: "Presupuesto preparado en {{houseName}}",
      budgetMonthSeededFromPreviousBody:
        "{{yearMonth}}: el presupuesto por categorías ya está guardado en tu hogar ({{copiedCount}} líneas copiadas desde {{sourceYearMonth}}). Solo te pedimos que revises y ajustes los importes en Presupuesto si hace falta.",
      vaultWarrantyExpiring7dTitle: "Garantía próxima a vencer",
      vaultWarrantyExpiring7dBody:
        "«{{itemName}}» en «{{houseName}}» tiene la garantía hasta el {{endDate}} (quedan unos 7 días).",
      upcomingExpenseTomorrowTitle: "Cargos previstos para mañana en {{houseName}}",
      upcomingExpenseTomorrowBody:
        "Tienes {{count}} gasto(s) pendiente(s) con fecha {{tomorrowDate}}. Revisa el diario para ir con margen.",
    },
    devicesPage: {
      subtitle: "Controla dónde tienes la sesión iniciada.",
      intro:
        "Cada fila es una sesión activa. Puedes cerrar sesión en un dispositivo concreto o en todos los demás a la vez.",
      thisDevice: "Este dispositivo",
      currentBadge: "Actual",
      lastActive: "Última actividad",
      sessionStarted: "Sesión iniciada",
      expires: "Caduca",
      ip: "IP",
      browser: "Navegador o app",
      unknownBrowser: "Desconocido",
      signOutDevice: "Cerrar sesión aquí",
      signOutDeviceConfirm: "¿Cerrar sesión en este dispositivo? Si es el que usas ahora, volverás al inicio de sesión.",
      signOutOthers: "Cerrar sesión en el resto",
      signOutOthersConfirm: "¿Cerrar sesión en todos los demás dispositivos? Esta sesión permanecerá activa.",
      loadError: "No se pudieron cargar las sesiones.",
      actionError: "No se pudo completar la acción. Inténtalo de nuevo.",
      empty: "No hay sesiones activas.",
    },
  },
  mobile: {
    common: {
      cancel: "Cancelar",
      confirm: "Confirmar",
      skip: "Omitir",
      ok: "Aceptar",
      changesSaved: "Cambios guardados.",
      retry: "Reintentar",
      loadError: "No se pudieron cargar los datos.",
      signingOut: "Cerrando sesión…",
      signOutConfirmTitle: "Cerrar sesión",
      signOutConfirmMessage: "¿Seguro que quieres cerrar sesión en este dispositivo?",
      signOutFailedTitle: "No se pudo cerrar sesión",
      signOutFailed:
        "Comprueba la conexión e inténtalo de nuevo. Si el problema continúa, cierra la app y vuelve a abrirla.",
    },
    session: {
      checking: "Comprobando sesión…",
    },
    api: {
      errors: {
        network:
          "Sin conexión o el servidor no responde. Comprueba tu red e inténtalo de nuevo.",
        generic: "No se pudo completar la operación. Inténtalo de nuevo.",
        serverUnavailable: "El servicio no está disponible en este momento. Inténtalo más tarde.",
        missingConfig: "La URL de la API no está configurada. Revisa la instalación o el entorno.",
      },
    },
    tabs: {
      home: "Inicio",
      houses: "Casas",
      scan: "Escaneo",
      budget: "Presup.",
      vault: "Bóveda",
    },
    shell: {
      comingSoon:
        "Esta sección estará disponible próximamente. Mientras tanto puedes usar el panel desde la web.",
    },
    home: {
      title: "HomeExpense",
      subtitle: "Tu espacio financiero del hogar, con la claridad de Estate Ledger.",
      heroEyebrow: "SALDO BANCARIO INICIAL",
      heroTitle: "Bienvenido",
      heroSubtitle: "Gestiona gastos y presupuesto con calma editorial.",
      heroTrend: "Sincronizado con la API",
      heroRemainingLabel: "{{amount}} estimado disponible",
      openSheet: "Abrir panel inferior",
      openPaginatedDemo: "Notificaciones (API)",
      healthChecking: "API: comprobando…",
      healthOk: "API: en línea",
      healthFail: "API: sin respuesta",
      insightTitle: "Dentro de la media",
      insightBody:
        "Tu ritmo de gasto este mes es estable. Abre el diario para ver el detalle.",
      insightBadge: "Eficiencia",
      insightValue: "Excelente",
      insightValueGood: "Excelente",
      insightValueFair: "En límite",
      insightValueOver: "Superado",
      upcomingCardBadge: "Próximo gasto",
      upcomingCardBodyGeneric: "Cargo previsto en el periodo.",
      upcomingCardNone: "Sin gastos pendientes",
      budgetCardTitle: "Presupuesto mensual",
      budgetCardCompleted: "{{percent}}% completado",
      budgetCardTotal: "Total: {{amount}}",
      monthSummaryTitle: "Resumen del mes",
      monthSummaryPersonalLabel: "Personal",
      monthSummarySharedLabel: "Común",
      monthSummaryTotalLabel: "Total a aportar",
      monthSummaryForecastPrefix: "Previsto: {{amount}}",
      houseSectionTitle: "Presupuesto de la casa",
      personalSectionTitle: "Gasto personal",
      quickActionsTitle: "Acciones rápidas",
      quickExpense: "Registrar gasto",
      quickScan: "Escanear ticket",
      quickMembers: "Miembros",
      quickFinancings: "Financiaciones",
      quickFabA11y: "Abrir menú de acciones rápidas",
      quickFabCloseA11y: "Cerrar menú de acciones rápidas",
      quickFabLabel: "Acciones",
      quickFabCloseLabel: "Cerrar",
      openJournal: "Ver diario",
      openJournalHeaderA11y: "Abrir diario",
      financingsShortcutTitle: "Financiaciones",
      financingsShortcutSubtitle: "Préstamos y cuotas del hogar activo.",
      analyticsShortcutTitle: "Tendencias del hogar",
      analyticsShortcutSubtitle: "Gráficas de pagado vs plan y señales por categoría.",
      openingBalanceLabel: "Saldo inicial del mes",
      openingBalanceMock: "2.450,00 €",
      defaultHouseName: "Casa principal",
      notificationsA11y: "Notificaciones",
      profileA11y: "Perfil",
      houseSelectorA11y: "Seleccionar hogar activo",
      noHouseTitle: "Sin hogares",
      noHouseBody:
        "Crea o únete a un hogar desde la web para ver aquí gastos, presupuesto y bóveda sincronizados con la API.",
      chooseHouseTitle: "Hogar activo",
      recentTitle: "Próximos gastos",
      upcomingViewAll: "Ver todos",
      goToChangePassword: "Cambiar contraseña",
      budgetSetupUrgentTitle: "Aún no hay referencia de presupuesto del mes",
      budgetSetupUrgentBody:
        "Sin líneas por categoría ni tope mensual del hogar, el progreso es difícil de leer. Configúralo en Presupuesto.",
      budgetSetupUrgentCta: "Ir a presupuesto",
      budgetSetupCapOnlyTitle: "Tienes tope mensual: añade categorías",
      budgetSetupCapOnlyBody:
        "El tope orienta el ritmo de gasto. Repartir por categoría da un control más claro.",
      budgetSetupCapOnlyCta: "Planificar por categoría",
    },
    journal: {
      title: "Diario financiero",
      searchPlaceholder: "Buscar en el diario…",
      fabA11y: "Añadir gasto",
      fabLabel: "Añadir gasto",
      filterAll: "Todos",
      filterHousehold: "Hogar",
      filterPersonal: "Personal",
      filterGroceries: "Supermercado",
      filterUtilities: "Servicios",
      filterInsurance: "Seguros",
      groupMonth: "Octubre 2025",
      expense1Title: "Compra semanal",
      expense2Title: "Electricidad",
      expense1Meta: "12 oct · ",
      expense2Meta: "8 oct · ",
      expense1Amount: "142,50 €",
      expense2Amount: "89,20 €",
      memberYou: "Tú",
      badgePaid: "Pagado",
      badgeUnpaid: "Pendiente",
      empty: "No hay gastos que coincidan con la búsqueda o el filtro.",
      emptyTitle: "Sin gastos todavía",
      emptyDescription:
        "Anota compras y recibos para ver el historial por meses y llevar el control del hogar.",
      emptyFilteredTitle: "Sin resultados",
      emptyCta: "Añadir gasto",
    },
    expenseQuickActions: {
      menuA11y: "Más acciones sobre el gasto",
      markPaidChip: "Marcar pagado",
      adjustActualChip: "Importe real",
      paidZeroChip: "Pagado a 0 €",
      paidZeroConfirmTitle: "Liquidar a importe 0",
      paidZeroConfirmMessage:
        "Se guardará el importe real en 0 € y el gasto quedará como pagado. Sirve para meses en los que ahorras la cuota (p. ej. bimensual): no cuenta como salida de efectivo este mes; la cuota efectiva irá en el mes en que pagues.",
      paidZeroSuccess: "Gasto liquidado a 0 € y marcado como pagado.",
      actualAmountPlaceholder: "0,00",
    },
    expenseDetail: {
      screenTitle: "Detalle del gasto",
      totalAmountLabel: "IMPORTE TOTAL",
      dateLabel: "Fecha",
      categoryLabel: "Categoría",
      scopeLabel: "Ámbito",
      paymentMethodLabel: "Método de pago",
      notesLabel: "Notas",
      paidByLabel: "Pagado por",
      recurringLabel: "Recurrencia",
      proratedLabel: "Prorrateo mensual",
      splitsTitle: "DISTRIBUCIÓN DEL COSTE",
      splitPercent: "{{percent}} % de contribución",
      markAsPaidCta: "Marcar como pagado",
      markAsUnpaidCta: "Marcar como pendiente",
      markingPaid: "Marcando…",
      markingUnpaid: "Deshaciendo…",
      markPaidSuccess: "Gasto marcado como pagado.",
      markUnpaidSuccess: "Gasto marcado como pendiente.",
      editCta: "Editar",
      noCategoryLabel: "Sin categoría",
      noNotesLabel: "Sin notas",
      confirmMarkPaid: "Confirmar pago",
      confirmMarkPaidMessage: "¿Marcar este gasto como pagado?",
      confirmMarkUnpaid: "Deshacer pago",
      confirmMarkUnpaidMessage: "¿Volver a marcar este gasto como pendiente?",
      errorMarkPaid: "No se pudo marcar como pagado. Inténtalo de nuevo.",
      errorMarkUnpaid: "No se pudo deshacer el pago. Inténtalo de nuevo.",
      cycleLabel: "Ciclo",
      projectionLabel: "Proyección",
      perMonth: "mes",
      actualCostLabel: "Coste real",
      realCostLabel: "Importe real registrado",
      updateCostTitle: "Actualizar coste real",
      updateCostCta: "Actualizar coste",
      updateCostError: "No se pudo actualizar el coste. Inténtalo de nuevo.",
      updateCostSuccess: "Coste real actualizado.",
      receiptLabel: "Ticket",
      receiptAttached: "Ticket adjunto",
      receiptNotAttached: "Sin ticket adjunto",
      receiptViewCta: "Ver ticket",
      receiptAttachCta: "Adjuntar ticket",
      receiptReplaceCta: "Reemplazar ticket",
      receiptSourceTitle: "Adjuntar ticket",
      receiptSourceMessage: "Elige cómo quieres adjuntar el ticket.",
      receiptSourceCamera: "Tomar foto",
      receiptSourceGallery: "Elegir de galería",
      receiptSourceDocument: "Elegir documento",
      receiptUpdating: "Subiendo ticket…",
      receiptUpdatedSuccess: "Ticket actualizado.",
      receiptUpdateError: "No se pudo actualizar el ticket. Inténtalo de nuevo.",
      deleteCta: "Eliminar gasto",
      deleteConfirmTitle: "Eliminar gasto",
      deleteConfirmMessage: "¿Eliminar este gasto? Esta acción no se puede deshacer.",
      deleteError: "No se pudo eliminar el gasto. Inténtalo de nuevo.",
      deleteSuccess: "Gasto eliminado.",
      deleting: "Eliminando…",
      freqMonthly: "Mensual",
      freqBimonthly: "Bimensual",
      freqQuarterly: "Trimestral",
      freqSemiannual: "Semestral",
      freqAnnual: "Anual",
      methodCard: "Tarjeta",
      methodCash: "Efectivo",
      methodAccount: "Cuenta",
      budgetSetupBannerTitle: "Sin referencia de presupuesto este mes",
      budgetSetupBannerBody:
        "Este mes no tiene plan por categorías ni tope mensual del hogar. Configúralo en Presupuesto.",
      budgetSetupBannerCta: "Ir a presupuesto",
    },
    expenseSeries: {
      screenTitle: "Serie recurrente",
    },
    houses: {
      screenTitle: "Casas y miembros",
      subtitle: "Activa un hogar para ver gastos y presupuesto asociados.",
      managementEyebrow: "Gestión",
      heroDescription:
        "Organiza tus propiedades y controla cómo se reparten los gastos entre las personas del hogar con precisión.",
      sampleHouseName: "Casa principal",
      activeBadge: "Activa",
      membersCta: "Miembros e invitaciones",
      budgetHint: "Presupuesto del mes al 68 %",
      budgetHintLive: "Avance del mes: {{percent}} % · gasto acumulado {{spent}}",
      empty: "Aún no tienes hogares. Crea uno ahora para empezar.",
      emptyTitle: "Aún no tienes hogares",
      emptyDescription:
        "Crea tu primer hogar para sincronizar gastos, presupuesto y bóveda con el resto del equipo.",
      setActiveHint: "Pulsa la tarjeta para usar este hogar en toda la app.",
      createScreenTitle: "Nueva casa",
      createSubtitle: "Dale un nombre a tu hogar para empezar a registrar gastos.",
      nameLabel: "Nombre del hogar",
      namePlaceholder: "Ej. Casa principal, Familia García…",
      createCta: "Crear casa",
      creating: "Creando…",
      createSuccess: "Casa creada correctamente.",
      createError: "No se pudo crear la casa. Inténtalo de nuevo.",
      financialProfileCta: "Perfil financiero",
      financingsCta: "Financiaciones",
      financialProfileScreenTitle: "Perfil financiero",
      financialProfileSubtitle: "Saldo inicial de referencia, moneda y tope mensual del hogar.",
      financialProfileFinancingsCta: "Ver financiaciones del hogar",
      openingBalanceLabel: "Saldo inicial (cuenta)",
      openingBalancePlaceholder: "0,00",
      currencyLabel: "Moneda (ISO 4217)",
      currencyPlaceholder: "EUR",
      monthlyBudgetLabel: "Tope mensual (opcional)",
      monthlyBudgetPlaceholder: "0,00",
      saveProfileCta: "Guardar perfil",
      savingProfile: "Guardando…",
      saveProfileSuccess: "Perfil financiero actualizado.",
      saveProfileError: "No se pudo guardar. Inténtalo de nuevo.",
      invalidAmount: "Introduce un importe válido (0 o mayor).",
      membersScreenTitle: "Miembros del hogar",
      membersSubtitle: "Personas con acceso a este hogar y sus roles.",
      memberRoleOwner: "Propietario",
      memberRoleAdmin: "Administrador",
      memberRoleMember: "Miembro",
      memberSince: "Desde {{date}}",
      invitePendingTitle: "Invitaciones pendientes",
      inviteExpires: "Caduca el {{date}}",
      inviteExpired: "Caducada",
      inviteEmpty: "No hay invitaciones pendientes.",
      membersEmpty: "No hay miembros en este hogar.",
      inviteCta: "Invitar miembro",
      inviteComingSoon: "Las invitaciones se gestionan desde la web. Próximamente disponible en la app.",
      inviteEmailSection: "Invitar por correo",
      inviteRoleLabel: "Rol",
      inviteEmailLabel: "Correo del invitado",
      inviteEmailPlaceholder: "nombre@ejemplo.com",
      inviteSendCta: "Enviar invitación",
      inviteSending: "Enviando…",
      inviteSentSuccess: "Invitación enviada. El invitado recibirá un correo.",
      inviteError: "No se pudo enviar la invitación. Inténtalo de nuevo.",
      revokeInviteCta: "Revocar",
      revokeInviteConfirm: "¿Revocar esta invitación? La persona no podrá unirse con ese enlace.",
      revokeInviteSuccess: "Invitación revocada.",
      revokeInviteError: "No se pudo revocar. Inténtalo de nuevo.",
      residentsTitle: "Residentes",
      manageAllCta: "Gestionar todos",
      splitAllocationTitle: "Reparto del presupuesto",
      allocatedBudgetLabel: "Presupuesto asignado",
      perMonthSuffix: "/ mes",
      adjustSplitCta: "Ajustar reparto",
      soloMemberHint: "Solo tú en esta propiedad.",
      inviteCompactCta: "Invitar",
      inviteCardTitle: "Invitar miembro",
      inviteCardBody:
        "Comparte gastos fácilmente invitando a otras personas a tu hogar.",
      inviteCardCta: "Ir a miembros e invitar",
      proTipTitle: "Consejo de ahorro",
      proTipBody:
        "Los hogares con repartos ajustados al consumo real suelen ahorrar más en suministros. Revisa el presupuesto mensual con calma.",
      proTipLink: "Ver presupuesto del hogar",
      settingsA11y: "Ajustes del hogar",
      useAsActiveCta: "Usar como hogar activo",
      setActiveSuccess: "Hogar activo actualizado.",
      removeMemberCta: "Quitar",
      removeMemberConfirm: "¿Quitar a esta persona del hogar?",
      removeMemberSuccess: "Miembro eliminado del hogar.",
      removeMemberError: "No se pudo quitar al miembro. Inténtalo de nuevo.",
      changeRoleCta: "Cambiar rol",
      changeRoleTitle: "Nuevo rol",
      roleUpdatedSuccess: "Rol actualizado.",
      roleUpdateError: "No se pudo cambiar el rol. Inténtalo de nuevo.",
      inviteAdminOnly: "Solo propietarios y administradores pueden invitar.",
    },
    budget: {
      screenTitle: "Presupuesto prorrateado",
      subtitle: "Comparación planificado frente al gasto real del hogar activo.",
      heroMainTitle: "Dinero necesario",
      heroMainAccent: "este mes",
      netBalanceLabel: "Balance neto estimado",
      netBalanceMock: "624,30 €",
      progressLabel: "Consumido del tope mensual",
      progressPercent: "{{percent}}%",
      rowCategory: "Categoría",
      rowBudgeted: "Planificado",
      rowActual: "Real",
      demoUtilitiesBudgeted: "120 €",
      demoUtilitiesActual: "89 €",
      demoInsuranceBudgeted: "85 €",
      demoInsuranceActual: "85 €",
      emptyRows: "No hay planificación de presupuesto por categoría para este mes.",
      emptyPerformanceTitle: "Sin datos de rendimiento",
      emptyPerformanceDescription:
        "Define importes planificados por categoría para comparar con lo gastado.",
      proratedTotalLabel: "Total prorrateado",
      moneyRequiredLabel: "Total requerido",
      remanentLabel: "Remanente {{amount}}",
      proratedInstallmentType: "Cuota",
      proratedInstallmentMonthly: "Mensual",
      proratedItemTotal: "Total {{amount}}",
      proratedMoveHint: "Cuota mensual que puedes reservar para pagos anuales.",
      adjustGoalsCta: "Ajustar objetivos",
      fabEditBudget: "Editar Presupuesto",
      fabCreateBudget: "Crear Presupuesto",
      performanceSectionTitle: "Presupuestado vs real",
      performanceSectionSubtitle: "Comparación planificado frente al gasto real.",
      performanceSectionPlanNote:
        "«Plan» es el importe que guardas al editar el presupuesto por categoría; no se rellena solo con la suma de las previsiones de tus cargos. «Real» suma los gastos ya pagados en el mes (usa el coste real si lo indicaste).",
      filtersCta: "Filtros",
      exportPdfCta: "Export PDF",
      exportPdfComingSoon: "La exportación PDF estará disponible próximamente.",
      editByCategoryTitle: "Planificación por categoría",
      editByCategorySubtitle: "Define el importe planificado de cada categoría para este mes.",
      createCategoryCta: "Nueva categoría",
      createCategoryTitle: "Crear categoría",
      createCategorySubtitle: "Añade una categoría para usarla en gastos y presupuesto.",
      categoryCreatedSuccess: "Categoría creada correctamente.",
      deleteCategoryA11y: "Eliminar categoría {{name}}",
      deleteCategoryConfirmTitle: "Eliminar categoría",
      deleteCategoryConfirmMessage:
        "¿Seguro que quieres eliminar «{{name}}»? Los gastos que la usaban quedarán sin categoría y las líneas de presupuesto asociadas se eliminarán.",
      deleteCategoryCta: "Eliminar",
      deleteCategorySuccess: "Categoría eliminada.",
      deleteCategoryError: "No se pudo eliminar la categoría. Inténtalo de nuevo.",
    },
    analytics: {
      screenTitle: "Tendencias del hogar",
      openFromBudget: "Tendencias y gráficas",
      backToBudget: "Presupuesto",
      subtitle: "Últimos {{count}} meses — pagado vs plan y señales por categoría.",
      empty:
        "No hay gastos pagados en este periodo. Las tendencias aparecerán cuando registres pagos.",
      chartTitle: "Por mes",
      legendPaid: "Pagado",
      legendPlanned: "Planificado",
      legendAvg: "Media pagada",
      chartScrollHint:
        "Puedes deslizar el gráfico a los lados para ver todos los meses; los más recientes están a la derecha.",
      chartYAxisA11y: "Eje vertical de importes, de cero a {{max}}.",
      peakTitle: "Mes pico",
      peakBody: "{{month}} · {{amount}}",
      peakNone: "Sin datos",
      avgTitle: "Media pagada / mes activo",
      avgHint: "Se excluyen los meses sin gasto pagado.",
      topCategories: "Más gasto acumulado por categoría (últimos {{count}} meses)",
      otherCategories: "Otras categorías: {{amount}}",
      overspendTitle: "Mayor sobrepaso acumulado (últimos {{count}} meses)",
      overspendBody:
        "«{{name}}»: {{amount}} por encima del plan en meses con partida, acumulado en los últimos {{count}} meses.",
      reviewTitle: "Revisar (a menudo por encima)",
      reviewHint: "En ≥80% de los meses con plan había sobrepaso (con historial suficiente).",
      reviewEmpty: "Ninguna en este periodo.",
      reduceTitle: "Ajustar plan a la baja (a menudo por debajo)",
      reduceHint: "En ≥80% de los meses con plan había gasto por debajo del plan.",
      reduceEmpty: "Ninguna en este periodo.",
    },
    vault: {
      screenTitle: "Bóveda de garantías",
      subtitle: "Garantías y activos con fecha de caducidad.",
      searchPlaceholder: "Buscar en la bóveda…",
      alertTitle: "Próximo vencimiento",
      alertBody: "La garantía del frigorífico caduca en 45 días.",
      alertBodyLive: "La garantía de «{{name}}» está próxima a vencer. Revisa la documentación en la web.",
      itemFridge: "Frigorífico · cocina",
      itemInsurance: "Seguro hogar",
      completionLabel: "Inventario completado",
      empty: "No hay elementos en la bóveda de este hogar.",
      emptyTitle: "Tu bóveda está vacía",
      emptyDescription:
        "Registra electrodomésticos, muebles y pólizas con fechas de garantía. Te avisaremos antes de que venzan.",
      emptyFilterTitle: "Nada en esta categoría",
      emptyFilterDescription:
        "Prueba otra pestaña o añade un activo que encaje en esta sección.",
      detailScreenTitle: "Bóveda · Activo",
      detailBadge: "Registro",
      detailTitle: "Detalles del activo",
      detailSubtitle: "Información original de compra y garantía.",
      detailAssetTypeLabel: "Categoría",
      detailPurchaseDateLabel: "Fecha de compra",
      detailWarrantyEndsLabel: "Vence garantía",
      detailNotesLabel: "Notas",
      detailDocumentLabel: "Comprobante",
      detailDocumentPresent: "Documento adjunto",
      detailDocumentMissing: "Sin comprobante",
      detailNoAssetType: "Sin categoría",
      detailNoPurchaseDate: "No especificada",
      detailNoWarrantyEnds: "Sin vencimiento",
      detailNoNotes: "Sin notas",
      detailEditCta: "Editar",
      detailWarrantyExpired: "Garantía vencida",
      detailWarrantyActiveDays: "{{days}} días restantes",
      createScreenTitle: "Añadir a la bóveda",
      editScreenTitle: "Editar activo",
      saveSuccess: "Activo guardado en la bóveda.",
      editSuccess: "Activo actualizado correctamente.",
      saveError: "No se pudo guardar el activo. Inténtalo de nuevo.",
      deleteCta: "Eliminar activo",
      deleteConfirm: "¿Eliminar este activo de la bóveda? Esta acción no se puede deshacer.",
      deleteSuccess: "Activo eliminado de la bóveda.",
      deleteError: "No se pudo eliminar. Inténtalo de nuevo.",
      nameLabel: "Nombre del artículo",
      namePlaceholder: "Ej. Lavadora, Seguro hogar…",
      assetTypeLabel: "Categoría",
      assetTypePlaceholder: "Ej. Electrodoméstico, Póliza…",
      purchaseDateLabel: "Fecha de compra",
      warrantyEndsLabel: "Fin de garantía",
      notesLabel: "Notas",
      notesPlaceholder: "Detalles del contrato, referencias…",
      saveCta: "Guardar en la bóveda",
      saving: "Guardando…",
      heroEyebrow: "Almacenamiento de documentos",
      heroTitle: "Bóveda",
      heroDescription:
        "Gestiona garantías y pólizas del hogar con tranquilidad. Te avisaremos antes de que venza tu protección.",
      criticalEyebrow: "Alerta crítica",
      criticalBodyUrgent:
        "La garantía de «{{name}}» vence en {{days}} días. Renueva o verifica la cobertura ahora.",
      criticalBodyExpired: "La garantía de «{{name}}» está vencida. Revisa o renueva la cobertura.",
      criticalCtaDetails: "Ver detalles",
      criticalCtaExtend: "Ampliar plan",
      statTotalLabel: "Activos totales",
      statDocsLabel: "Con comprobante",
      filterAll: "Todos",
      sectionCount: "{{count}} elementos",
      cardPurchaseLabel: "Fecha de compra",
      cardExpiresLabel: "Vencimiento",
      viewReceiptCta: "Ver comprobante",
      registerNewCta: "Registrar nuevo activo",
      badgeExpired: "Vencida",
      badgeEndsToday: "Vence hoy",
      badgeDaysLeft: "{{count}} días",
      badgeMonthsLeft: "{{count}} meses",
      badgeYearsLeft: "{{count}} años",
      badgeSafe: "En orden",
      badgeNoDate: "Sin fecha",
      formEyebrow: "Registro",
      formTitle: "Datos del nuevo activo",
      formSubtitle:
        "Protege tu inversión indicando la información original de la compra.",
      uploadSectionLabel: "Comprobante de compra",
      uploadTitle: "Subir prueba de compra",
      uploadSubtitle: "Elige un archivo o una foto de la galería",
      uploadTypesHint: "JPG, PNG, WebP, GIF o PDF · máximo {{maxMb}} MB",
      uploadPickFilesCta: "Elegir archivo",
      uploadPickGalleryCta: "Galería",
      uploadUploading: "Subiendo…",
      uploadAttachedHint: "Adjunto: {{fileName}}",
      uploadRemoveCta: "Quitar",
      uploadRemoveA11y: "Quitar documento adjunto",
      uploadErrorMime: "Tipo no permitido. Usa imagen (JPG, PNG, WebP, GIF) o PDF.",
      uploadErrorSize: "El archivo supera el tamaño máximo ({{maxMb}} MB).",
      uploadPermissionDenied:
        "Sin permiso para acceder a las fotos. Actívalo en los ajustes del dispositivo.",
      uploadErrorGeneric: "No se pudo subir el archivo. Inténtalo de nuevo.",
      uploadSuccessReady: "Archivo listo. Guarda el activo para conservar el comprobante.",
      uploadSoon: "La subida de archivos estará disponible próximamente.",
      completionStatusLabel: "Estado del registro en bóveda",
      saveDisclaimer:
        "Al guardar, autorizas el cifrado seguro en la nube de tus documentos de compra y fechas de garantía.",
      moreOptionsA11y: "Más opciones",
    },
    journalFilters: {
      screenTitle: "Filtros",
      badge: "Refinar vista",
      title: "Filtros",
      subtitle: "Ajusta los criterios para encontrar gastos específicos.",
      categoriesSection: "Categorías",
      membersSection: "Miembros",
      dateRangeSection: "Rango de fechas",
      paidStatusSection: "Estado de pago",
      scopeSection: "Ámbito",
      fromLabel: "Desde",
      toLabel: "Hasta",
      paidOnly: "Solo pagados",
      unpaidOnly: "Solo pendientes",
      allStatuses: "Todos",
      applyCta: "Aplicar filtros",
      resetCta: "Restablecer",
      applying: "Aplicando…",
    },
    profile: {
      screenTitle: "Cuenta",
      settingsItem: "Configuración de cuenta",
      settingsItemSub: "Gestiona tus datos personales",
      notificationsItem: "Notificaciones",
      notificationsItemSub: "Alertas y actualizaciones",
      securityItem: "Privacidad y seguridad",
      securityItemSub: "Contraseña y permisos",
      helpItem: "Ayuda y soporte",
      helpItemSub: "Preguntas frecuentes y contacto",
      signOutItem: "Cerrar sesión",
      signOutItemSub: "Salir de forma segura",
      deleteAccountItem: "Eliminar cuenta",
      deleteAccountItemSub: "Borrar permanentemente tus datos y acceso",
      deleteAccountConfirmTitle: "¿Eliminar cuenta?",
      deleteAccountConfirmMessage:
        "Se eliminarán de forma permanente tu usuario y datos personales. Esta acción no se puede deshacer.",
      deleteAccountConfirmCta: "Eliminar cuenta",
      deleteAccountError: "No se pudo eliminar la cuenta. Inténtalo de nuevo.",
      totalAssetsLabel: "Total activos",
      activeBudgetsLabel: "Meses con presupuesto",
      comingSoon: "Próximamente disponible.",
    },
    settings: {
      hubTitle: "Cuenta",
      hubSubtitle: "Gestiona tu perfil, seguridad y preferencias de la app.",
      backToAccount: "Volver a cuenta",
      cardPersonalTitle: "Datos personales",
      cardPersonalHint: "Nombre, correo e idioma de la interfaz.",
      cardPasswordTitle: "Contraseña",
      cardPasswordHint: "Actualiza tu contraseña de acceso.",
      cardSessionsTitle: "Dispositivos y sesiones",
      cardSessionsHint: "Sesiones activas en otros navegadores o dispositivos.",
      cardPasskeysTitle: "Passkeys y biometría",
      cardPasskeysHint: "Registro passkey (pre-auth) y acceso biométrico local.",
      cardNotificationsTitle: "Notificaciones",
      cardNotificationsHint: "Push, avisos y dispositivos registrados.",
      cardSupportTitle: "Ayuda y soporte",
      cardSupportHint: "Recuperar acceso y recursos de ayuda.",
      cardAppearanceTitle: "Apariencia",
      cardAppearanceHint: "Tema claro, oscuro o según el sistema.",
      avatarChangeA11y: "Cambiar foto de perfil",
      avatarUploading: "Subiendo foto…",
      clearAvatar: "Quitar foto de perfil",
      avatarPickError: "Elige una imagen (JPG, PNG, WebP o GIF).",
      avatarUploadError: "No se pudo actualizar la foto. Inténtalo de nuevo.",
      footerBrand: "HomeExpense",
      personalTitle: "Datos personales",
      personalSubtitle: "Tu correo no se puede cambiar desde la app.",
      emailLabel: "Correo electrónico",
      emailHint: "Para cambiar el correo, contacta con soporte.",
      nameField: "Nombre visible",
      profileFormSectionTitle: "Perfil e idioma",
      saveButton: "Guardar cambios",
      saving: "Guardando…",
      savedHint: "Cambios guardados.",
      appearanceTitle: "Apariencia",
      appearanceSubtitle: "Elige cómo se muestra la app en tu dispositivo.",
      appearanceLight: "Claro",
      appearanceDark: "Oscuro",
      appearanceSystem: "Sistema",
      appearanceCurrent: "Seleccionado",
      sessionsTitle: "Dispositivos y sesiones",
      sessionsSubtitle: "Sesiones con acceso a tu cuenta.",
      sessionsIntro:
        "Puedes cerrar sesión en otros dispositivos. Si cierras la sesión de este dispositivo, tendrás que volver a iniciar sesión.",
      sessionsLoadError: "No se pudieron cargar las sesiones.",
      sessionsEmpty: "No hay otras sesiones registradas.",
      sessionsSignOutOthers: "Cerrar otras sesiones",
      sessionsSignOutDevice: "Cerrar sesión",
      sessionsThisDevice: "Este dispositivo",
      sessionsCurrentBadge: "Actual",
      sessionsLastActive: "Última actividad",
      sessionsStarted: "Inicio de sesión",
      sessionsExpires: "Caduca",
      sessionsIp: "IP",
      sessionsUnknownUa: "Cliente desconocido",
      sessionsSignOutDeviceConfirm: "¿Cerrar sesión en este dispositivo?",
      sessionsSignOutOthersConfirm: "¿Cerrar todas las sesiones excepto esta?",
      sessionsActionError: "No se pudo completar la acción. Inténtalo de nuevo.",
      passkeysTitle: "Passkeys y biometría",
      passkeysSubtitle:
        "Gestiona el desbloqueo rápido en este dispositivo y prepara el contexto pre-auth para registrar passkeys.",
      passkeysBiometricTitle: "Verificar desbloqueo biométrico",
      passkeysBiometricHint: "Comprueba Face ID / Touch ID / huella con tu sesión actual.",
      passkeysBiometricSuccess: "Biometría validada correctamente en este dispositivo.",
      passkeysBiometricError: "No se pudo validar la biometría. Inténtalo de nuevo.",
      passkeysContextTitle: "Generar contexto passkey pre-auth",
      passkeysContextHint:
        "Genera un contexto temporal firmado para continuar el alta de passkey en clientes compatibles.",
      passkeysContextSuccess: "Contexto passkey pre-auth generado.",
      passkeysContextSuccessHint: "Caduca en {{seconds}} segundos.",
      passkeysContextError: "No se pudo generar el contexto passkey.",
      notificationsTitle: "Notificaciones",
      notificationsSubtitle: "Preferencias de avisos y registro push en este dispositivo.",
      pushSectionTitle: "Notificaciones push",
      activatePush: "Activar o renovar push",
      pushWorking: "Las notificaciones push están disponibles en este dispositivo.",
      pushConfigMissing: "Firebase no está configurado en la app.",
      pushPermissionDenied: "Permiso de notificaciones denegado. Actívalo en Ajustes del sistema.",
      pushRegisterSuccess: "Dispositivo registrado para notificaciones push.",
      pushFailureNoSession: "Inicia sesión para poder registrar este dispositivo.",
      pushFailurePermissionRequest: "No se pudo completar la solicitud de permiso de notificaciones.",
      pushFailureIosRemote: "No se pudo activar notificaciones remotas en este iPhone. Revisa Firebase y los certificados APNs.",
      pushFailureToken: "No se obtuvo el token de notificaciones. Comprueba Google Play services, datos o la configuración de Firebase.",
      pushRegisterError: "No se pudo registrar el dispositivo para notificaciones.",
      prefsSectionTitle: "Preferencias",
      prefPushMaster: "Activar notificaciones push",
      prefHouseInvites: "Hogar e invitaciones",
      prefExpenses: "Gastos y pagos",
      prefBudget: "Presupuesto",
      prefReminders: "Recordatorios",
      prefScanDocs: "Escaneo y documentos",
      prefDigests: "Resúmenes",
      prefUpcomingCharges: "Cargos de mañana (no pagados)",
      devicesSectionTitle: "Dispositivos registrados",
      noPushDevices: "Ningún dispositivo registrado para push.",
      lastSeenLabel: "Última vez visto",
      devicePushLabel: "Push activo",
      removeDevice: "Eliminar",
      removeDeviceConfirm: "¿Eliminar este dispositivo de la lista?",
      platformWeb: "Web",
      platformIos: "iOS",
      platformAndroid: "Android",
      supportTitle: "Ayuda y soporte",
      supportSubtitle: "Recursos para recuperar el acceso o resolver dudas.",
      supportIntro:
        "Si no puedes acceder a tu cuenta, puedes solicitar un enlace para restablecer la contraseña.",
      supportForgotTitle: "¿Olvidaste tu contraseña?",
      supportForgotBody: "Te enviaremos un enlace seguro al correo que indiques.",
      supportForgotCta: "Solicitar enlace de recuperación",
    },
    scan: {
      screenTitle: "Escaneo con IA",
      subtitle: "Captura un ticket y extrae el importe automáticamente.",
      scanningHint: "Encuadra el documento en la zona de escaneo.",
      openGallery: "Galería",
      manualEntry: "Entrada manual",
      recentTitle: "Escaneos recientes",
      empty: "Todavía no hay escaneos de tickets para este hogar.",
      emptyTitle: "Sin escaneos recientes",
      emptyDescription:
        "Sube una foto del ticket y extraeremos el importe automáticamente con IA.",
      emptyCta: "Subir ticket",
      statusPending: "Pendiente",
      statusProcessing: "Procesando",
      statusCompleted: "Completado",
      statusFailed: "Error",
      uploading: "Subiendo imagen…",
      registering: "Registrando escaneo…",
      uploadSuccess: "Ticket recibido. Lo procesaremos en unos segundos.",
      pollHint: "Analizando ticket con IA…",
      createExpenseFromScan: "Crear gasto con estos datos",
      extractedTitle: "Datos detectados",
      extractedMerchant: "Comercio",
      extractedDate: "Fecha",
      extractedTotal: "Total",
      extractionErrorFallback: "No se pudo interpretar el ticket.",
      permissionDenied: "Permiso de cámara denegado. Actívalo en Ajustes.",
      permissionBlocked: "Permiso bloqueado. Ve a Ajustes > Privacidad > Cámara para habilitarlo.",
    },
    addExpense: {
      createTitle: "Nuevo gasto",
      editTitle: "Editar gasto",
      saveSuccess: "Gasto guardado correctamente.",
      editSuccess: "Gasto actualizado correctamente.",
      saveError: "No se pudo guardar el gasto. Inténtalo de nuevo.",
      invalidAmount: "Introduce un importe válido.",
      invalidCurrency: "Indica una moneda ISO válida (3 letras, ej. EUR).",
      titleOrCategoryRequired: "Añade un título o selecciona una categoría.",
      titleLabel: "Concepto",
      titlePlaceholder: "Ej. Compra semanal, Luz…",
      amountLabel: "Importe",
      dateLabel: "Fecha",
      categoryLabel: "Categoría",
      categoryNone: "Sin categoría",
      paymentMethodLabel: "Método de pago",
      paymentMethodNone: "Sin especificar",
      paymentMethodCard: "Tarjeta",
      paymentMethodCash: "Efectivo",
      paymentMethodAccount: "Cuenta",
      paidByLabel: "Pagado por",
      paidByDefault: "Cuenta del hogar",
      paidNowLabel: "Marcar como pagado ahora",
      notesLabel: "Notas",
      notesPlaceholder: "Detalles o referencias…",
      receiptLabel: "Comprobante",
      receiptAttachCta: "Adjuntar PDF o imagen",
      receiptAttachedHint: "Archivo listo para guardar con el gasto.",
      receiptClearA11y: "Quitar comprobante",
      receiptUploading: "Subiendo…",
      receiptUploadError: "No se pudo subir el archivo. Inténtalo de nuevo.",
      receiptLinkedExisting: "Ya hay un comprobante; puedes sustituirlo o quitarlo.",
      isRecurringLabel: "Gasto recurrente",
      recurrenceKindLabel: "Tipo de recurrencia",
      recurrenceMonthlyFixed: "Mensual fijo",
      recurrenceSeries: "Serie personalizada",
      frequencyLabel: "Frecuencia",
      freqMonthly: "Mensual",
      freqBimonthly: "Bimensual",
      freqQuarterly: "Trimestral",
      freqSemiannual: "Semestral",
      freqAnnual: "Anual",
      endsOnLabel: "Fecha de fin",
      endsOnPlaceholder: "YYYY-MM-DD (opcional)",
      proratedLabel: "Prorratear importe",
      monthlyFixedHint: "Se materializarán {{count}} cuotas mensuales automáticamente.",
      submitCreate: "Crear gasto",
      submitCreateRecurring: "Crear serie recurrente",
      submitSave: "Guardar cambios",
      beforeSubmitSummaryTitle: "Resumen antes de guardar",
      summaryConcept: "Concepto",
      summaryUnnamed: "(sin título)",
      summaryTotalAmount: "Importe total",
      summaryInstallmentAmount: "Cuota en el periodo (reparto)",
      summaryDate: "Fecha",
      summaryCategory: "Categoría",
      summaryPaymentMethod: "Método de pago",
      summaryPaidBy: "Quién pagó",
      summarySharedSplit: "Reparto (esta cuota)",
      summaryScheduleTitle: "Cargos en el diario (vista previa)",
      summaryMoreDates: "… y {{count}} más",
      enterAmountForSplit: "Indica un importe válido para ver el reparto.",
      sharedPreviewRangeInvalid: "Revisa las fechas de inicio y fin de la serie.",
      sharedPreviewFinalizeRecurrence: "Completa la recurrencia (importe y fechas) para ver el reparto.",
      flows: {
        launcherTitle: "¿Qué quieres hacer?",
        launcherSubtitle:
          "Elige un recorrido según el tipo de gasto que quieras registrar.",
        manualOnlyBanner:
          "Introduce los datos clave del gasto: importe, fecha y método de pago.",
        intentQuickTitle: "Registrar un gasto puntual",
        intentQuickDescription:
          "Para un cargo que ya hiciste: importe, categoría y cómo pagaste. El resto solo si lo necesitas.",
        intentRecurringTitle: "Gasto fijo cada mes",
        intentRecurringDescription:
          "Una cuota que se repite (alquiler, comunidad…). Te guiamos paso a paso para que sepas qué estás configurando.",
        intentSeriesTitle: "Serie con fechas o prorrateo",
        intentSeriesDescription:
          "Cuotas con fecha de fin, varias frecuencias o reparto del total entre meses. Incluye resumen antes de guardar.",
        quickExpandMore: "Más opciones (concepto, notas, compartido…)",
        repeatLastFlowCta: "Continuar con: {{flowLabel}}",
        navNext: "Siguiente",
        navBack: "Atrás",
        navReview: "Ver resumen",
        editStepA11y: "Editar paso {{step}}",
        monthlyWizard: {
          step1Title: "¿Qué gasto es?",
          step1Help: "Nombre o categoría. Así lo reconocerás en el diario cada mes.",
          step2Title: "¿Cuánto pagas al mes?",
          step2Help: "Un solo importe por mes. Lo repetiremos en cada cuota.",
          step3Title: "¿Cuándo empieza?",
          step3Help: "Primera fecha de cargo o el día en que quieres que aparezca en el diario.",
          step4Title: "¿Cómo lo pagas?",
          step4Help: "Tarjeta, efectivo o cuenta: tú indicas cómo sale el dinero (todo manual).",
          step5Title: "¿Lo compartís en casa?",
          step5Help: "Quién paga y si el gasto es compartido entre miembros.",
          reviewTitle: "Así quedará tu gasto fijo",
          reviewHelp: "Revisa los datos. Puedes volver atrás y cambiar cualquier paso.",
        },
        seriesWizard: {
          step1Title: "Concepto e importe",
          step1Help:
            "Qué es este gasto y el importe según el modo (por mes o total del periodo, según elijas después).",
          step2Title: "Cada cuánto se repite",
          step2Help: "Mensual, trimestral, anual… La frecuencia define cómo aparecen las cuotas en el diario.",
          step3Title: "Desde cuándo y hasta cuándo",
          step3Help:
            "Fecha de inicio y, si aplica, última cuota. Sin fecha de fin, la serie puede seguir abierta según las reglas del formulario.",
          step4Title: "Prorrateo",
          step4Help:
            "Si repartes un total entre varias cuotas, aquí se explica el reparto. Sin banco: confirmas tú los importes.",
          step5Title: "Reparto entre personas",
          step5Help: "Compartido y quién paga. Verás una vista previa cuando el importe y las fechas estén listos.",
          reviewTitle: "Resumen de la serie",
          reviewHelp: "Comprueba cuotas y fechas antes de crear la serie.",
        },
      },
    },
    notifications: {
      screenTitle: "Notificaciones",
      empty: "Sin notificaciones",
      emptyHint: "Cuando haya novedades del hogar aparecerán aquí.",
      markAllRead: "Marcar todo leído",
      markAllReadConfirm: "¿Marcar todas las notificaciones como leídas?",
      markAllReadSuccess: "Todo marcado como leído.",
      markReadError: "No se pudo marcar como leída.",
    },
    pendingInvitations: {
      screenTitle: "Invitaciones pendientes",
      subtitle: "Hogares a los que te han invitado. Acepta para unirte y empezar a compartir gastos.",
      empty: "Sin invitaciones pendientes",
      emptyHint: "Cuando alguien te invite a un hogar aparecerá aquí.",
      inviterLine: "Te invita: {{inviterName}}",
      expiresLabel: "Caduca el {{date}}",
      acceptCta: "Aceptar e unirme",
      accepting: "Uniéndome…",
      acceptSuccess: "Te has unido al hogar correctamente.",
      acceptError: "No se pudo aceptar la invitación. Inténtalo de nuevo.",
    },
    financings: {
      screenTitle: "Financiaciones",
      empty: "Sin financiaciones",
      emptyHint: "Registra préstamos y cuotas fijas para seguir su progreso.",
      detailScreenTitle: "Detalle de financiación",
      installmentLabel: "Cuota: {{amount}}",
      termProgressLabel: "{{percent}}% del plazo",
      contractStartLabel: "Inicio del contrato",
      endsOnLabel: "Última cuota prevista",
      remainingLabel: "Pendiente estimado: {{amount}}",
      openSeriesCta: "Ver gastos recurrentes",
      noSeries: "Sin gastos recurrentes vinculados",
      aiSummaryTitle: "Resumen IA",
      deleteCta: "Eliminar financiación",
      deleteConfirm: "¿Eliminar esta financiación? Esta acción no se puede deshacer.",
      deleteSuccess: "Financiación eliminada.",
      deleteError: "No se pudo eliminar. Inténtalo de nuevo.",
      remainingEstimatedShort: "Pendiente estimado",
      addDocumentCta: "Añadir documento",
      removeDocumentA11y: "Quitar documento",
      docMaxReached: "Máximo {{max}} documentos.",
      docUploadError: "No se pudo subir el archivo. Inténtalo de nuevo.",
      docTooLarge: "El archivo es demasiado grande.",
      docMimeNotAllowed: "Tipo de archivo no permitido.",
    },
    demo: {
      title: "Notificaciones",
      itemLabel: "Elemento {{index}}",
      listSubtitle: "GET /notifications (cursor). Inicia sesión desde Inicio para cargar datos.",
      loadingMore: "Cargando más…",
      sheetTitle: "Ejemplo de bottom sheet",
      sheetBody:
        "Este modal usa @gorhom/bottom-sheet. Úsalo para filtros, formularios cortos o acciones contextuales.",
      sheetCloseHint: "Desliza hacia abajo o pulsa fuera para cerrar.",
      fetchError: "No se pudo cargar la lista.",
      needSession: "No hay sesión. Ve a Inicio e inicia sesión con email y contraseña.",
      emptyNotifications: "No hay notificaciones en esta página.",
      notificationType: "Tipo: {{type}}",
    },
    auth: {
      screenTitle: "Iniciar sesión",
      signInTitle: "Bienvenido de nuevo",
      signInSubtitle: "Introduce tus credenciales para acceder a tu libro.",
      registerTitle: "Crear cuenta",
      registerSubtitle: "Empieza hoy con claridad financiera en tu hogar.",
      registerScreenTitle: "Registro",
      forgotScreenTitle: "Recuperar acceso",
      forgotBadge: "Portal de seguridad",
      forgotTitle: "Recupera tu acceso financiero.",
      forgotTitleLead: "Recupera tu",
      forgotTitleAccent: "acceso financiero.",
      forgotSubtitle: "Acceso",
      forgotBody:
        "Indica el correo de tu cuenta HomeExpense. Te enviaremos un enlace seguro para restablecer tu contraseña.",
      forgotEmailLabel: "Correo registrado",
      forgotPlaceholderEmail: "p. ej. curador@estate.com",
      contactSupport: "Contactar con soporte",
      contactSupportHint: "Pronto podrás contactar desde la app. Mientras tanto, usa la ayuda en la web.",
      forgotDecorLetters: "LE",
      forgotDecorBrand: "Estate Ledger",
      forgotDecorVersion: "v. 4.0.2",
      subtitle: "Usa la misma cuenta que en la web (email y contraseña).",
      nameLabel: "Nombre completo",
      namePlaceholder: "Tu nombre",
      emailLabel: "Correo electrónico",
      emailPlaceholder: "nombre@ejemplo.com",
      passwordLabel: "Contraseña",
      passwordPlaceholder: "••••••••",
      confirmPasswordLabel: "Confirmar contraseña",
      signInCta: "Entrar al libro",
      registerCta: "Crear cuenta gratis",
      sendRecoveryCta: "Enviar enlace de recuperación",
      signingIn: "Entrando…",
      registering: "Creando cuenta…",
      sendingRecovery: "Enviando…",
      signOut: "Cerrar sesión",
      goToSignIn: "Iniciar sesión",
      goToRegister: "Crear cuenta",
      goToForgot: "¿Olvidaste la contraseña?",
      backToSignIn: "Volver al inicio de sesión",
      orContinueWith: "O continúa con",
      orRegisterWithEmail: "O regístrate con correo",
      socialGoogle: "Google",
      socialApple: "Apple",
      signInWithApple: "Iniciar sesión con Apple",
      signUpWithApple: "Registrarse con Apple",
      signInWithPasskey: "Iniciar sesión con Passkey",
      signUpWithPasskey: "Registrarse con Passkey",
      continuingWithGoogle: "Conectando con Google…",
      continuingWithApple: "Conectando con Apple…",
      continuingWithPasskey: "Conectando con Passkey…",
      socialApplePending: "El inicio de sesión con Apple en móvil estará disponible pronto.",
      socialPasskey: "Passkey",
      errorGoogleFailed: "No se pudo iniciar sesión con Google. Inténtalo de nuevo.",
      errorGoogleCancelled: "Se canceló el inicio de sesión con Google.",
      errorGoogleConfigMissing:
        "Google Sign-In no está configurado en esta app. Revisa GOOGLE_WEB_CLIENT_ID.",
      errorAppleFailed: "No se pudo iniciar sesión con Apple. Inténtalo de nuevo.",
      errorAppleCancelled: "Se canceló el inicio de sesión con Apple.",
      errorAppleConfigMissing:
        "Apple Sign-In no está configurado en esta app. Revisa APPLE_CLIENT_ID y APPLE_APP_BUNDLE_IDENTIFIER.",
      errorPasskeyFailed: "No se pudo iniciar sesión con Passkey. Inténtalo de nuevo.",
      errorPasskeyUnavailable: "Tu dispositivo no soporta Passkeys para este flujo.",
      biometricPromptTitle: "Desbloquear HomeExpense",
      socialUnavailable: "El inicio de sesión social en móvil estará disponible pronto.",
      termsAgree: "Acepto los",
      termsLink: "Términos del servicio",
      privacyLink: "Política de privacidad",
      termsConnector: "y la",
      termsRequired: "Debes aceptar los términos y la privacidad para continuar.",
      alreadyHaveAccount: "¿Ya tienes cuenta?",
      newHere: "¿Nuevo aquí?",
      sessionActive: "Sesión iniciada",
      forgotSentHint:
        "Si existe una cuenta con ese correo, recibirás un enlace en breve.",
      signIn: "Entrar",
      errorInvalidCredentials: "Email o contraseña incorrectos.",
      errorGeneric: "No se pudo iniciar sesión. Inténtalo de nuevo.",
      errorPasswordMismatch: "Las contraseñas no coinciden.",
      errorSignUpFailed: "No se pudo crear la cuenta. Inténtalo de nuevo.",
      errorRecoveryFailed: "No se pudo enviar el enlace. Inténtalo de nuevo.",
      registerCheckEmail:
        "Revisa tu correo para verificar la cuenta antes de iniciar sesión.",
      changePasswordScreenTitle: "Cambiar contraseña",
      changePasswordBadge: "CONFIGURACIÓN DE SEGURIDAD",
      changePasswordTitle: "Actualiza tus credenciales",
      changePasswordSubtitle:
        "Protege tus datos financieros con una contraseña única y robusta.",
      currentPasswordLabel: "Contraseña actual",
      currentPasswordPlaceholder: "••••••••••••",
      newPasswordLabel: "Nueva contraseña",
      newPasswordPlaceholder: "••••••••••••",
      confirmNewPasswordLabel: "Confirmar nueva contraseña",
      changePasswordCta: "Actualizar contraseña",
      changingPassword: "Actualizando…",
      changePasswordSuccess: "Contraseña actualizada",
      changePasswordSuccessHint:
        "Tu contraseña se ha cambiado. Seguirás conectado en este dispositivo.",
      errorChangePasswordFailed:
        "No se pudo actualizar la contraseña. Inténtalo de nuevo.",
      errorChangePasswordWrong:
        "La contraseña actual es incorrecta. Verifica e inténtalo de nuevo.",
      passwordStrengthWeak: "Débil",
      passwordStrengthFair: "Regular",
      passwordStrengthGood: "Buena",
      passwordStrengthStrong: "Fuerte",
      passwordStrengthLabel: "Nivel de seguridad",
      securityTipsTitle: "Consejos de seguridad",
      securityTip1Title: "Unicidad",
      securityTip1Body:
        "No reutilices contraseñas de otros servicios financieros.",
      securityTip2Title: "Complejidad",
      securityTip2Body:
        "Mezcla mayúsculas, números y símbolos especiales ($@!).",
      changePasswordSignOutHint:
        "Cambiar la contraseña cerrará tu sesión en otros dispositivos.",
    },
  },
  marketing: marketingEs,
  api: {
    errors: {
      validationFailed: "Los datos enviados no son válidos",
      unauthorized: "No autenticado",
      forbidden: "No tienes permiso para esta acción",
      internal: "Error interno del servidor",
      fallback: "Solicitud incorrecta",
      validation: {
        expectedString: "El campo {{property}} debe ser texto",
        invalidUuid: "El campo {{property}} debe ser un UUID válido",
        expectedInt: "El campo {{property}} debe ser un número entero",
        expectedBoolean: "El campo {{property}} debe ser verdadero o falso",
        expectedArray: "El campo {{property}} debe ser una lista",
        expectedObject: "El campo {{property}} debe ser un objeto",
        invalidEnum: "El valor de {{property}} no está permitido",
        min: "El campo {{property}} no cumple el mínimo requerido",
        max: "El campo {{property}} no cumple el máximo permitido",
        pattern: "El formato de {{property}} no es válido",
        invalidField: "El campo {{property}} no es válido",
      },
      houses: {
        nameRequired: "El nombre es obligatorio",
        createFailed: "No se pudo crear la casa",
        notFound: "Casa no encontrada",
        nameEmpty: "El nombre no puede estar vacío",
        userIdRequired: "El userId es obligatorio",
        cannotAssignOwnerViaApi: "No se puede asignar el rol propietario por esta vía",
        userAlreadyMember: "El usuario ya es miembro",
        memberNotFound: "Miembro no encontrado",
        atLeastOneOwner: "Debe existir al menos un propietario",
        defaultSplitBpsRange: "defaultSplitBps debe estar entre 0 y 10000",
        cannotRemoveSoleOwner: "No se puede eliminar al único propietario",
        financialProfileNotFound: "Perfil financiero no encontrado",
        currencyIso: "La moneda debe ser ISO 4217 (3 letras)",
        invitationEmailInvalid: "Correo de invitación no válido",
        invitationNotFound: "Invitación no encontrada o ya no es válida",
        invitationTokenInvalid: "Enlace de invitación no válido",
        invitationEmailMismatch: "Inicia sesión con el correo al que se envió la invitación",
        invitationEmailFailed: "No se pudo enviar la invitación. Revisa la configuración de correo.",
        invitationUserMissing: "Cuenta de usuario no encontrada",
      },
      access: {
        notMember: "No perteneces a esta casa",
        adminRequired: "Se requiere rol de administrador o propietario",
        ownerOnly: "Solo el propietario puede realizar esta acción",
      },
      expenses: {
        userNotMember: "El usuario {{userId}} no es miembro de esta casa",
        notFound: "Gasto no encontrado",
        titleRequired: "El título es obligatorio",
        amountMinorInvalid: "El importe (amountMinor) no es válido",
        categoryInvalidForHouse: "Categoría no válida para esta casa",
        categoryInvalid: "Categoría no válida",
        currencyIso: "La moneda debe ser ISO 4217",
        currencyInvalid: "Moneda no válida",
        createFailed: "No se pudo crear el gasto",
        titleEmpty: "El título no puede estar vacío",
        splitSumMustBe10000: "La suma de shareBps debe ser exactamente 10000",
        splitDuplicateUser:
          "El reparto no puede incluir al mismo usuario más de una vez (userId duplicado en splits)",
        splitShareBpsInvalid: "shareBps inválido (debe estar entre 0 y 10000)",
        recurrenceEndsBeforeStart: "La fecha de fin debe ser igual o posterior a la fecha de inicio",
        recurrenceNoOccurrences: "No hay fechas de cargo en el intervalo indicado",
        proratedRequiresEndsOn:
          "Con frecuencia mensual, el prorrateo de un total requiere fecha de fin de la serie (o usa bimestral/trimestral… para repartir el total de cada periodo en meses sueltos sin fin)",
        seriesNotFound: "Serie recurrente no encontrada",
        seriesFixedSplitsRequired:
          "Indica el reparto (splits) para una serie con reparto fijo",
        seriesSplitsOnlyForFixed:
          "Solo puedes enviar reparto fijo si la serie está en modo reparto fijo",
        seriesEndsBeforeAnchor:
          "La fecha de fin no puede ser anterior a la primera fecha de la serie",
        seriesEndedWithFutureEndsOn:
          "Para acortar la serie en el futuro, solo cambia la fecha de fin sin marcarla como finalizada.",
      },
      financings: {
        notFound: "Financiación no encontrada",
        titleRequired: "El título es obligatorio",
        installmentInvalid: "La cuota mensual no es válida",
        contractStartInvalid: "La fecha de inicio del contrato no es válida",
        endsOnInvalid: "La fecha de fin no es válida",
        endsBeforeContract: "La fecha de fin no puede ser anterior al inicio del contrato",
        noFutureInstallments:
          "No hay cuotas futuras a partir de hoy con esas fechas. Revisa el inicio del contrato y el fin.",
        aiRequiresDocuments: "El análisis con IA requiere al menos un documento",
        documentStorageKeyRequired: "Cada documento debe tener una clave de almacenamiento",
        summarizationFailed: "No se pudo generar el resumen del contrato con IA",
        createFailed: "No se pudo registrar la financiación",
      },
      budgets: {
        yearMonthFormat: "yearMonth debe tener formato YYYY-MM",
        plannedAmountInvalid: "plannedAmountMinor no es válido",
        categoryNotInHouse: "La categoría {{categoryId}} no pertenece a esta casa",
        categoryNotFound: "Categoría no encontrada",
      },
      vault: {
        notFound: "Elemento no encontrado",
        nameRequired: "El nombre es obligatorio",
        nameEmpty: "El nombre no puede estar vacío",
        assetTypeInvalid: "Categoría no válida. Elige una de las categorías permitidas.",
      },
      receiptScans: {
        notFound: "Escaneo no encontrado",
        imageKeyRequired: "imageStorageKey es obligatorio",
        statusInvalid: "Estado (status) no válido",
        dailyLimitExceeded:
          "Has alcanzado el límite diario de escaneos con IA ({{limit}}). Vuelve a intentarlo mañana.",
        extractionFailed: "No se pudo leer el ticket. Prueba con otra foto más nítida.",
        extractionDisabled:
          "La extracción con IA no está configurada en el servidor. Contacta con el administrador.",
        imageTooLarge: "La imagen es demasiado grande para procesarla con IA.",
        unsupportedMedia: "Formato de archivo no soportado para este escaneo.",
      },
      categories: {
        nameRequired: "El nombre es obligatorio",
        notFound: "Categoría no encontrada",
        nameEmpty: "El nombre no puede estar vacío",
      },
      dashboard: {
        yearMonthInvalid: "yearMonth no es válido",
        yearMonthFormat: "yearMonth debe ser YYYY-MM",
      },
      notifications: {
        notFound: "Notificación no encontrada",
        deviceNotFound: "Dispositivo no encontrado",
      },
      storage: {
        fileRequired: "Se requiere un archivo",
        mimeNotAllowed: "Tipo de archivo no permitido: {{mime}}",
        fileTooLarge: "Archivo demasiado grande. Máximo {{maxMb}} MB",
        keyRequired: "Falta la clave del archivo en almacenamiento",
        invalidKey: "Clave de archivo no válida o no permitida",
      },
      account: {
        userNotFound: "Usuario no encontrado",
        deletionBlockedReferences:
          "No se pudo completar la baja por referencias de datos pendientes. Contacta con soporte.",
      },
      publicContact: {
        sendFailed: "No se pudo enviar el mensaje. Inténtalo más tarde.",
        serviceUnavailable:
          "El envío de mensajes no está disponible ahora (servicio de correo no configurado o no disponible).",
      },
    },
  },
};

// Exportar también como default para next-intl
export default es;
